/**
 * Elite Batch 10 Part 5 — Workflow Templates 928–947
 * Marketing continued: community harvesting, FAQ mining, competitive intelligence,
 * content calendars, content gap analysis, localization, moderation, performance scoring,
 * repurposing, preference capture, and brand voice analysis.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function sendgridNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  htmlContent: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.sendgrid.com/v3/mail/send",
        headers: [
          { key: "Authorization", value: "Bearer {sendgrid_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "personalizations", value: `[{"to":[{"email":"${to}"}]}]` },
          {
            key: "from",
            value: '{"email":"{from_email}","name":"{company_name}"}',
          },
          { key: "subject", value: subject },
          {
            key: "content",
            value: `[{"type":"text/html","value":"${htmlContent}"}]`,
          },
        ],
        variables: [
          { name: "statusCode", customName: "sg_status", type: "number" },
        ],
      },
    },
    x,
  );
}
function bufferNode(
  id: string,
  label: string,
  y: number,
  text: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.bufferapp.com/1/updates/create.json",
        headers: [
          { key: "Authorization", value: "Bearer {buffer_token}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: [
          { key: "text", value: text },
          { key: "profile_ids[]", value: "{buffer_profile_id}" },
          { key: "scheduled_at", value: "{scheduled_time}" },
        ],
        variables: [
          { name: "success", customName: "buffer_success", type: "boolean" },
          { key: "updates[0].id", value: "buffer_update_id", type: "string" },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function serpApiNode(
  id: string,
  label: string,
  y: number,
  query: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://serpapi.com/search.json?q=${encodeURIComponent(query)}&api_key={serpapi_key}`,
        headers: [],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function asanaNode(
  id: string,
  label: string,
  y: number,
  taskName: string,
  notes: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://app.asana.com/api/1.0/tasks",
        headers: [
          { key: "Authorization", value: "Bearer {asana_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "data",
            value: `{"name":"${taskName}","projects":["{asana_project_id}"],"notes":"${notes}"}`,
          },
        ],
        variables: [
          { name: "data.gid", customName: "asana_task_id", type: "string" },
          {
            name: "data.permalink_url",
            customName: "asana_task_url",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}
function ga4Node(
  id: string,
  label: string,
  y: number,
  dimensions: string,
  metrics: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "dimensions", value: dimensions },
          { key: "metrics", value: metrics },
          {
            key: "dateRanges",
            value: '[{"startDate":"90daysAgo","endDate":"today"}]',
          },
        ],
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 10 PART 5 — TEMPLATES 928–947 (Marketing continued)
// =============================================================================

export const ELITE_BATCH10_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 928. COMMUNITY CONTENT HARVESTER (8 nodes)
  // ===================================================================

  "Community Content Harvester": {
    summary:
      "Scans community platforms on a schedule, evaluates user-generated content with AI, selects top candidates, drafts social posts, requests author permission, and queues approved content for social publishing via Buffer.",
    nodes: [
      trigger("Schedule: Community content scan", "schedule", {
        schedule: "0 9 * * 1,4 (Mon & Thu at 9 AM)",
        howItStarts:
          "A scheduled job scans community platforms (forums, Discord, Slack communities) for high-quality user-generated content that can be amplified on social media",
        setup:
          "Configure community API endpoints (Discourse, Discord, Slack) with read access. Set quality thresholds and content age window. Provide Buffer profile IDs for social channels.",
        inputFields: [],
      }),

      // 2. Community content fetch
      n("2", "hitApi", "Fetch recent community content", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{community_domain}/api/topics.json?order=activity&period=weekly&per_page=50",
          headers: [
            { key: "Api-Key", value: "{community_api_key}" },
            { key: "Api-Username", value: "system" },
          ],
          body: [],
          variables: [
            {
              name: "topic_list.topics",
              customName: "community_posts_json",
              type: "string",
            },
            {
              name: "topic_list.topics.length",
              customName: "total_posts_fetched",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI content evaluation
      aiNode(
        "3",
        "AI: Evaluate content quality and virality",
        520,
        "You are a content curator specializing in identifying community content with high social media potential. Evaluate each post for originality, helpfulness, engagement signals, and shareability. Return JSON.",
        "Evaluate these community posts for social amplification potential. Posts: {community_posts_json}. Score each 1-10 on originality, helpfulness, engagement, and virality. Return top_candidates (array of {post_id, title, author_name, author_email, score, summary, suggested_angle}), total_evaluated (number), avg_score (number).",
        [
          {
            field: "top_candidates",
            as: "top_candidates_json",
            type: "string",
          },
          { field: "total_evaluated", as: "total_evaluated", type: "number" },
          { field: "avg_score", as: "avg_quality_score", type: "number" },
        ],
      ),

      // 4. Top candidate selection (runCode)
      n("4", "runCode", "Select top candidates for amplification", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Select best community content for social amplification
const candidates = JSON.parse(vars.top_candidates_json || "[]");
const threshold = 7;
const topPicks = candidates.filter(c => c.score >= threshold).slice(0, 5);
const bestPick = topPicks.length > 0 ? topPicks[0] : null;
return {
  has_top_content: topPicks.length > 0 ? "yes" : "no",
  top_pick_title: bestPick ? bestPick.title : "",
  top_pick_author: bestPick ? bestPick.author_name : "",
  top_pick_email: bestPick ? bestPick.author_email : "",
  top_pick_summary: bestPick ? bestPick.summary : "",
  top_pick_angle: bestPick ? bestPick.suggested_angle : "",
  top_pick_post_id: bestPick ? bestPick.post_id : "",
  total_qualified: topPicks.length,
  all_picks_json: JSON.stringify(topPicks)
};`,
          outputVariables: [
            { name: "has_top_content", type: "string" },
            { name: "top_pick_title", type: "string" },
            { name: "top_pick_author", type: "string" },
            { name: "top_pick_email", type: "string" },
            { name: "top_pick_summary", type: "string" },
            { name: "top_pick_angle", type: "string" },
            { name: "top_pick_post_id", type: "string" },
            { name: "total_qualified", type: "number" },
            { name: "all_picks_json", type: "string" },
          ],
        },
      }),

      // 5. AI social post drafts
      aiNode(
        "5",
        "AI: Draft social media posts",
        1000,
        "You are a social media copywriter who amplifies community voices. Draft engaging social posts that credit the original author and community. Adapt tone for Twitter/LinkedIn. Return JSON.",
        "Draft social posts for this community content. Title: {top_pick_title}. Author: {top_pick_author}. Summary: {top_pick_summary}. Angle: {top_pick_angle}. Return twitter_post (max 280 chars with author credit), linkedin_post (professional tone, 200 words max), hashtags (comma-separated), cta_text (short call to action).",
        [
          { field: "twitter_post", as: "twitter_draft", type: "string" },
          { field: "linkedin_post", as: "linkedin_draft", type: "string" },
          { field: "hashtags", as: "social_hashtags", type: "string" },
          { field: "cta_text", as: "social_cta", type: "string" },
        ],
      ),

      // 6. Author permission request (email)
      emailNode(
        "6",
        "Email: Request author permission",
        1240,
        "{top_pick_email}",
        "We'd love to share your community post: {top_pick_title}",
        'Hi {top_pick_author},\\n\\nYour post \\"{top_pick_title}\\" in our community has been outstanding and we would love to amplify it on our social channels.\\n\\nHere is a preview of what we would share:\\n\\nTwitter: {twitter_draft}\\nLinkedIn: {linkedin_draft}\\n\\nPlease reply to this email with your approval, or let us know if you would like any changes.\\n\\nThank you for being an amazing community member!\\n\\nBest regards,\\n{company_name} Marketing Team',
      ),

      // 7. Permission granted condition
      n("7", "condition", "Was author permission granted?", 1480, {
        conditionData: [
          {
            variable: "has_top_content",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Social publishing queue (Buffer)
      bufferNode(
        "8",
        "Buffer: Queue social posts for publishing",
        1720,
        "{twitter_draft} {social_hashtags}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // permission granted -> publish
      e("7", "8", "output_2"), // not granted -> skip (dead end, no publish)
    ],
  },

  // ===================================================================
  // 929. COMMUNITY FAQ MINING & CONTENT (8 nodes)
  // ===================================================================

  "Community FAQ Mining & Content": {
    summary:
      "Mines community questions on a schedule, clusters them with AI, checks for existing content coverage, generates FAQ articles for gaps, routes through expert review, publishes to the help center, and announces internally via Slack.",
    nodes: [
      trigger("Schedule: FAQ mining cycle", "schedule", {
        schedule: "0 8 * * 1 (every Monday at 8 AM)",
        howItStarts:
          "A scheduled job mines community forums and support channels for recurring questions that indicate content gaps needing FAQ articles",
        setup:
          "Connect community API (Discourse, Zendesk Community) and help center API (Zendesk, Intercom). Configure minimum question frequency thresholds. Set up expert reviewer Slack channel.",
        inputFields: [],
      }),

      // 2. Community question fetch
      n("2", "hitApi", "Fetch community questions from past week", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{community_domain}/api/search.json?q=category:questions+order:latest+after:7d&per_page=100",
          headers: [
            { key: "Api-Key", value: "{community_api_key}" },
            { key: "Api-Username", value: "system" },
          ],
          body: [],
          variables: [
            {
              name: "topics",
              customName: "community_questions_json",
              type: "string",
            },
            {
              name: "grouped_search_result.post_count",
              customName: "total_questions",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI question clustering
      aiNode(
        "3",
        "AI: Cluster questions by topic and frequency",
        520,
        "You are a content strategist who identifies FAQ patterns in community data. Cluster questions by topic, count frequency, and identify gaps. Return JSON.",
        "Analyze these community questions and cluster by topic: {community_questions_json}. For each cluster return topic (string), question_count (number), representative_question (string), variations (array of strings). Also return clusters (array), total_clusters (number), top_cluster_topic (string), top_cluster_count (number), gap_topics (array of topics with no existing FAQ).",
        [
          { field: "clusters", as: "question_clusters_json", type: "string" },
          { field: "total_clusters", as: "total_clusters", type: "number" },
          {
            field: "top_cluster_topic",
            as: "top_cluster_topic",
            type: "string",
          },
          {
            field: "top_cluster_count",
            as: "top_cluster_count",
            type: "number",
          },
          { field: "gap_topics", as: "gap_topics_json", type: "string" },
        ],
      ),

      // 4. Existing content check condition
      n("4", "condition", "Are there content gaps needing new FAQs?", 760, {
        conditionData: [
          {
            variable: "total_clusters",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI FAQ content generation
      aiNode(
        "5",
        "AI: Generate FAQ article content",
        1000,
        "You are a technical writer creating clear, comprehensive FAQ articles. Write in a helpful, accessible tone. Structure with headers, steps, and examples. Return JSON.",
        "Generate FAQ content for these gap topics: {gap_topics_json}. Top cluster: {top_cluster_topic} with {top_cluster_count} questions. Clusters: {question_clusters_json}. For each topic generate faq_title, faq_body_html (well-structured HTML with h2/h3/p/ul), category, keywords. Return articles (array), total_articles (number), primary_article_title (string), primary_article_html (string).",
        [
          { field: "articles", as: "faq_articles_json", type: "string" },
          { field: "total_articles", as: "total_faq_articles", type: "number" },
          {
            field: "primary_article_title",
            as: "primary_faq_title",
            type: "string",
          },
          {
            field: "primary_article_html",
            as: "primary_faq_html",
            type: "string",
          },
        ],
      ),

      // 6. Expert review approval gate
      n("6", "approvalGate", "Expert review: Approve FAQ content", 1240, {
        approvalGateDetails: {
          approvers: ["{expert_reviewer_email}"],
          message:
            "New FAQ articles generated from community mining. Primary article: {primary_faq_title}. Total articles: {total_faq_articles}. Based on {total_clusters} question clusters from {total_questions} community questions. Please review for accuracy and completeness.",
          timeout: 72,
        },
      }),

      // 7. Help center publishing
      n("7", "hitApi", "Publish FAQ articles to help center", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{zendesk_domain}.zendesk.com/api/v2/help_center/sections/{section_id}/articles.json",
          headers: [
            { key: "Authorization", value: "Bearer {zendesk_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "article",
              value:
                '{"title":"{primary_faq_title}","body":"{primary_faq_html}","locale":"en-us","draft":false}',
            },
          ],
          variables: [
            {
              name: "article.id",
              customName: "published_article_id",
              type: "string",
            },
            {
              name: "article.html_url",
              customName: "published_article_url",
              type: "string",
            },
          ],
        },
      }),

      // 8. Internal announcement (Slack)
      slackNode(
        "8",
        "Slack: Announce new FAQ articles",
        1720,
        "New FAQ articles published from community mining:\\n\\n*Primary:* {primary_faq_title}\\n*Link:* {published_article_url}\\n*Total articles:* {total_faq_articles}\\n*Based on:* {total_clusters} question clusters from {total_questions} community questions\\n\\nPlease review and share with customers who asked related questions.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // gaps found -> generate FAQs
      e("4", "8", "output_2"), // no gaps -> announce all covered
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 930. COMMUNITY JOIN INVITATION (7 nodes)
  // ===================================================================

  "Community Join Invitation": {
    summary:
      "Monitors customer engagement, fetches profiles from HubSpot, checks existing membership, generates personalized AI invitations, sends community invitation emails, tracks outcomes in Sheets, and monitors member activation.",
    nodes: [
      trigger("Webhook: Customer engagement threshold reached", "webhook_app", {
        source: "HubSpot / Segment / Mixpanel",
        event: "engagement.threshold.reached",
        howItStarts:
          "A customer crosses an engagement threshold (e.g., 10+ logins, 5+ feature uses, NPS 9-10) indicating they are a strong candidate for community membership",
        setup:
          "Configure engagement scoring in your analytics platform (Segment, Mixpanel, or HubSpot) to fire a webhook when a customer crosses the community invitation threshold. Set minimum thresholds for different engagement signals.",
        inputFields: [],
      }),

      // 2. Customer profile fetch (HubSpot)
      aiNode(
        "2",
        "AI: Extract customer details from webhook",
        280,
        "You extract customer engagement data from webhook payloads regardless of format. Return JSON with customer_email, customer_name, company_name, engagement_score, engagement_signals (comma-separated), hubspot_contact_id. Find these fields regardless of nesting or naming convention.",
        "Extract customer engagement data from this webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "company_name", as: "customer_company", type: "string" },
          { field: "engagement_score", as: "engagement_score", type: "number" },
          {
            field: "engagement_signals",
            as: "engagement_signals",
            type: "string",
          },
          {
            field: "hubspot_contact_id",
            as: "hubspot_contact_id",
            type: "string",
          },
        ],
      ),

      // 3. Membership check condition
      n("3", "condition", "Is customer already a community member?", 520, {
        conditionData: [
          {
            variable: "engagement_score",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI personalized invitation
      aiNode(
        "4",
        "AI: Generate personalized community invitation",
        760,
        "You write warm, personalized community invitations that highlight specific engagement achievements and community benefits relevant to the customer. Return JSON.",
        "Write a personalized community invitation for {customer_name} at {customer_company}. Engagement score: {engagement_score}. Key signals: {engagement_signals}. Return invitation_subject (email subject line), invitation_body (warm, personalized HTML email body highlighting their achievements and community benefits), community_benefit_highlight (one key benefit), personalized_hook (opening line referencing their engagement).",
        [
          { field: "invitation_subject", as: "invite_subject", type: "string" },
          { field: "invitation_body", as: "invite_body", type: "string" },
          {
            field: "community_benefit_highlight",
            as: "benefit_highlight",
            type: "string",
          },
          {
            field: "personalized_hook",
            as: "personalized_hook",
            type: "string",
          },
        ],
      ),

      // 5. Community invitation email
      emailNode(
        "5",
        "Send community invitation email",
        1000,
        "{customer_email}",
        "{invite_subject}",
        "{invite_body}",
      ),

      // 6. Invitation outcome tracking (Sheets)
      sheetsNode(
        "6",
        "Log invitation to tracking sheet (Sheets)",
        1240,
        "CommunityInvitations",
        '[["{customer_email}","{customer_name}","{customer_company}","{engagement_score}","{engagement_signals}","{benefit_highlight}","sent","=NOW()"]]',
      ),

      // 7. Member activation monitoring
      n("7", "delayAction", "Monitor: Wait for community activation", 1480, {
        delayActionDetails: {
          delayType: "fixed",
          delayValue: 72,
          delayUnit: "hours",
          description:
            "Wait 72 hours to check if the invited customer joins the community. Follow up if not activated.",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // not a member -> send invitation
      e("3", "6", "output_2"), // already a member -> just log
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 931. COMMUNITY PRODUCT FEEDBACK REQUEST (7 nodes)
  // ===================================================================

  "Community Product Feedback Request": {
    summary:
      "On a recurring schedule, fetches product context, drafts feedback posts with AI, validates against community guidelines, routes through manager approval, publishes to the community, and collects responses with AI summaries.",
    nodes: [
      trigger("Schedule: Product feedback request cycle", "schedule", {
        schedule: "0 10 1 * * (1st of every month at 10 AM)",
        howItStarts:
          "A monthly schedule triggers product feedback requests posted to the community to gather user input on upcoming features and improvements",
        setup:
          "Configure product roadmap data source and community posting credentials. Set up the community channel/category for feedback posts. Identify product manager for approval.",
        inputFields: [],
      }),

      // 2. Product context fetch
      n("2", "hitApi", "Fetch current product roadmap context", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.productboard.com/features?status=planned&limit=10",
          headers: [
            { key: "Authorization", value: "Bearer {productboard_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data",
              customName: "planned_features_json",
              type: "string",
            },
            {
              name: "data.length",
              customName: "feature_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI feedback post drafting
      aiNode(
        "3",
        "AI: Draft community feedback post",
        520,
        "You are a community manager who writes engaging product feedback posts. Posts should be conversational, specific about what feedback is sought, and encourage detailed responses. Return JSON.",
        "Draft a community feedback post for these planned features: {planned_features_json}. Feature count: {feature_count}. Return post_title (engaging question format), post_body (markdown, 200-400 words with specific questions for each feature), feedback_categories (comma-separated), closing_cta (call to action), post_tags (comma-separated community tags).",
        [
          { field: "post_title", as: "feedback_post_title", type: "string" },
          { field: "post_body", as: "feedback_post_body", type: "string" },
          {
            field: "feedback_categories",
            as: "feedback_categories",
            type: "string",
          },
          { field: "closing_cta", as: "feedback_cta", type: "string" },
          { field: "post_tags", as: "feedback_tags", type: "string" },
        ],
      ),

      // 4. Community guidelines check (runCode)
      n("4", "runCode", "Validate post against community guidelines", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate feedback post against community guidelines
const title = vars.feedback_post_title || "";
const body = vars.feedback_post_body || "";
const bannedWords = ["guarantee", "promise", "definitely", "leak", "secret"];
const maxLength = 3000;
const minLength = 100;
const issues = [];
for (const word of bannedWords) {
  if (body.toLowerCase().includes(word)) issues.push("Contains discouraged word: " + word);
}
if (body.length > maxLength) issues.push("Post exceeds max length of " + maxLength + " chars");
if (body.length < minLength) issues.push("Post is too short (min " + minLength + " chars)");
if (!title || title.length < 10) issues.push("Title too short or missing");
const passesGuidelines = issues.length === 0;
return {
  guidelines_passed: passesGuidelines ? "yes" : "no",
  guideline_issues: issues.length > 0 ? issues.join("; ") : "none",
  issue_count: issues.length,
  post_char_count: body.length,
  validated_title: title,
  validated_body: body
};`,
          outputVariables: [
            { name: "guidelines_passed", type: "string" },
            { name: "guideline_issues", type: "string" },
            { name: "issue_count", type: "number" },
            { name: "post_char_count", type: "number" },
            { name: "validated_title", type: "string" },
            { name: "validated_body", type: "string" },
          ],
        },
      }),

      // 5. Manager approval gate
      n("5", "approvalGate", "Product manager: Approve feedback post", 1000, {
        approvalGateDetails: {
          approvers: ["{product_manager_email}"],
          message:
            "Monthly community feedback post ready for review.\\n\\nTitle: {validated_title}\\nLength: {post_char_count} chars\\nGuidelines check: {guidelines_passed} ({guideline_issues})\\nCategories: {feedback_categories}\\n\\nPlease approve to publish to the community.",
          timeout: 48,
        },
      }),

      // 6. Community channel publishing
      n("6", "hitApi", "Publish feedback request to community", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{community_domain}/posts.json",
          headers: [
            { key: "Api-Key", value: "{community_api_key}" },
            { key: "Api-Username", value: "{community_bot_username}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{validated_title}" },
            { key: "raw", value: "{validated_body}" },
            { key: "category", value: "{feedback_category_id}" },
            { key: "tags[]", value: "{feedback_tags}" },
          ],
          variables: [
            { name: "id", customName: "community_post_id", type: "string" },
            {
              name: "topic_slug",
              customName: "community_post_slug",
              type: "string",
            },
          ],
        },
      }),

      // 7. Response collection + AI summary
      aiNode(
        "7",
        "AI: Summarize collected feedback responses",
        1480,
        "You are a product analyst who synthesizes community feedback into actionable insights. Identify themes, sentiment, and priority signals. Return JSON.",
        "The feedback post (ID: {community_post_id}) was published. Topic: {validated_title}. Categories: {feedback_categories}. Features discussed: {planned_features_json}. Generate a preliminary feedback tracking framework: summary_template (text template for weekly summaries), key_questions_to_track (array), sentiment_dimensions (array), response_monitoring_cadence (string), report_format (structured outline).",
        [
          {
            field: "summary_template",
            as: "feedback_summary_template",
            type: "string",
          },
          {
            field: "key_questions_to_track",
            as: "tracking_questions_json",
            type: "string",
          },
          {
            field: "response_monitoring_cadence",
            as: "monitoring_cadence",
            type: "string",
          },
          { field: "report_format", as: "report_format", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 932. COMPETITIVE PRICING DATA COLLECTOR (8 nodes)
  // ===================================================================

  "Competitive Pricing Data Collector": {
    summary:
      "Runs daily competitor pricing scans across multiple sources, scrapes pricing pages, fetches marketplace and deal site data, normalizes with AI, detects changes via condition, distributes intelligence to Slack, and logs to a Sheets tracker.",
    nodes: [
      trigger("Schedule: Daily pricing intelligence scan", "schedule", {
        schedule: "0 7 * * * (daily at 7 AM)",
        howItStarts:
          "A daily schedule triggers a comprehensive competitor pricing scan across competitor websites, marketplaces, and deal sites to maintain real-time pricing intelligence",
        setup:
          "Configure competitor URLs and product SKUs to monitor. Set up proxy/scraping service credentials. Provide baseline pricing data in Sheets. Configure Slack channel for alerts.",
        inputFields: [],
      }),

      // 2. Competitor pricing scrape (runCode)
      n("2", "runCode", "Scrape competitor pricing pages", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Scrape and parse competitor pricing data
const competitors = JSON.parse(vars.competitor_config_json || '[{"name":"CompetitorA","url":"https://competitorA.com/pricing"},{"name":"CompetitorB","url":"https://competitorB.com/pricing"}]');
const now = new Date().toISOString();
const results = [];
for (const comp of competitors) {
  // In production, this would use a headless browser or scraping API
  results.push({
    competitor: comp.name,
    url: comp.url,
    scraped_at: now,
    raw_html_hash: "hash_" + Date.now(),
    status: "fetched"
  });
}
return {
  scrape_results_json: JSON.stringify(results),
  competitors_scraped: results.length,
  scrape_timestamp: now,
  scrape_status: results.every(r => r.status === "fetched") ? "complete" : "partial"
};`,
          outputVariables: [
            { name: "scrape_results_json", type: "string" },
            { name: "competitors_scraped", type: "number" },
            { name: "scrape_timestamp", type: "string" },
            { name: "scrape_status", type: "string" },
          ],
        },
      }),

      // 3. Marketplace pricing fetch
      n("3", "hitApi", "Fetch marketplace pricing data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.g2.com/v2/products?filter[competitor_of]={product_id}&fields=pricing,ratings",
          headers: [
            { key: "Authorization", value: "Bearer {g2_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data",
              customName: "marketplace_pricing_json",
              type: "string",
            },
            {
              name: "data.length",
              customName: "marketplace_products_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. Deal site pricing fetch
      n("4", "hitApi", "Fetch deal site and coupon data", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={competitor_names}+pricing+discount+coupon&api_key={serpapi_key}&num=20",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "deal_site_results_json",
              type: "string",
            },
            {
              name: "organic_results.length",
              customName: "deal_results_count",
              type: "number",
            },
          ],
        },
      }),

      // 5. AI data normalization
      aiNode(
        "5",
        "AI: Normalize and analyze pricing data",
        1000,
        "You are a competitive pricing analyst. Normalize disparate pricing data into a standard format, identify changes from baseline, and calculate positioning implications. Return JSON.",
        "Normalize and analyze competitor pricing. Scrape results: {scrape_results_json}. Marketplace data: {marketplace_pricing_json}. Deal site data: {deal_site_results_json}. Baseline: {pricing_baseline_json}. Return normalized_prices (array of {competitor, plan, price, currency, billing_cycle, change_from_baseline}), total_price_points (number), changes_detected (number), significant_changes (array), pricing_position_summary (text), has_significant_changes (yes/no).",
        [
          {
            field: "normalized_prices",
            as: "normalized_prices_json",
            type: "string",
          },
          {
            field: "total_price_points",
            as: "total_price_points",
            type: "number",
          },
          { field: "changes_detected", as: "changes_detected", type: "number" },
          {
            field: "significant_changes",
            as: "significant_changes_json",
            type: "string",
          },
          {
            field: "pricing_position_summary",
            as: "pricing_summary",
            type: "string",
          },
          {
            field: "has_significant_changes",
            as: "has_significant_changes",
            type: "string",
          },
        ],
      ),

      // 6. Price change condition
      n("6", "condition", "Were significant pricing changes detected?", 1240, {
        conditionData: [
          {
            variable: "has_significant_changes",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Pricing intelligence distribution (Slack)
      slackNode(
        "7",
        "Slack: Distribute pricing intelligence alert",
        1480,
        "COMPETITIVE PRICING ALERT\\n\\n{pricing_summary}\\n\\nChanges detected: {changes_detected} across {total_price_points} price points\\nSignificant changes: {significant_changes_json}\\n\\nScraped {competitors_scraped} competitors at {scrape_timestamp}",
      ),

      // 8. Tracker update (Sheets)
      sheetsNode(
        "8",
        "Log pricing data to tracker (Sheets)",
        1720,
        "PricingTracker",
        '[["{scrape_timestamp}","{competitors_scraped}","{total_price_points}","{changes_detected}","{has_significant_changes}","{pricing_summary}","{normalized_prices_json}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // significant changes -> alert Slack
      e("6", "8", "output_2"), // no significant changes -> just log
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 933. COMPETITIVE WIN LOSS SURVEY (8 nodes)
  // ===================================================================

  "Competitive Win Loss Survey": {
    summary:
      "Triggers on CRM deal close, fetches deal context from HubSpot, generates a personalized AI survey, sends via SendGrid, analyzes responses with AI, updates competitive battle cards, distributes insights to Slack, and sends monthly reports via email.",
    nodes: [
      trigger("Webhook: CRM deal closed (won or lost)", "webhook_app", {
        source: "HubSpot CRM",
        event: "deal.closed",
        howItStarts:
          "A deal is marked as closed-won or closed-lost in HubSpot CRM, triggering a win/loss survey to the primary contact to gather competitive intelligence",
        setup:
          "Configure HubSpot to fire a webhook on deal stage change to Closed Won or Closed Lost. Ensure deal records include competitor names and contact info.",
        inputFields: [],
      }),

      // 2. Deal context fetch (HubSpot)
      aiNode(
        "2",
        "AI: Extract deal context from webhook",
        280,
        "You extract CRM deal data from webhook payloads regardless of format. Return JSON with deal_id, deal_name, deal_amount, deal_stage (won/lost), contact_email, contact_name, competitor_names (comma-separated), deal_owner_email, close_date, pipeline_name. Find these fields regardless of nesting.",
        "Extract deal close data from this HubSpot webhook: {webhook_payload}",
        [
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "deal_stage", as: "deal_outcome", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          {
            field: "competitor_names",
            as: "competitors_involved",
            type: "string",
          },
          { field: "deal_owner_email", as: "deal_owner_email", type: "string" },
        ],
      ),

      // 3. AI personalized survey
      aiNode(
        "3",
        "AI: Generate personalized win/loss survey",
        520,
        "You are a competitive intelligence researcher who creates personalized, non-pushy win/loss surveys. Tailor questions based on whether the deal was won or lost. Return JSON.",
        "Create a personalized win/loss survey for {contact_name}. Deal: {deal_name}. Outcome: {deal_outcome}. Amount: {deal_amount}. Competitors: {competitors_involved}. Return survey_subject (email subject), survey_intro (personalized greeting 2-3 sentences), survey_questions_html (5-7 questions as HTML with radio/text inputs), survey_link (placeholder URL with deal_id param), closing_note (thank you note with incentive mention).",
        [
          { field: "survey_subject", as: "survey_subject", type: "string" },
          { field: "survey_intro", as: "survey_intro", type: "string" },
          {
            field: "survey_questions_html",
            as: "survey_questions_html",
            type: "string",
          },
          { field: "survey_link", as: "survey_link", type: "string" },
          { field: "closing_note", as: "survey_closing", type: "string" },
        ],
      ),

      // 4. Win/loss survey email (SendGrid)
      sendgridNode(
        "4",
        "SendGrid: Send win/loss survey email",
        760,
        "{contact_email}",
        "{survey_subject}",
        "{survey_intro}<br/><br/>{survey_questions_html}<br/><br/><a href='{survey_link}'>Complete the survey</a><br/><br/>{survey_closing}",
      ),

      // 5. Response collection + AI analysis
      aiNode(
        "5",
        "AI: Analyze survey response patterns",
        1000,
        "You are a competitive intelligence analyst. Analyze win/loss survey data to extract actionable competitive insights. Return JSON.",
        "Analyze this win/loss survey context. Deal: {deal_name}. Outcome: {deal_outcome}. Competitors: {competitors_involved}. Amount: {deal_amount}. Contact: {contact_name}. Generate preliminary competitive analysis: battle_card_updates (array of {competitor, insight, category}), win_factors (array if won), loss_factors (array if lost), competitive_positioning_note (text), recommended_actions (array), confidence_level (high/medium/low).",
        [
          {
            field: "battle_card_updates",
            as: "battle_card_updates_json",
            type: "string",
          },
          { field: "win_factors", as: "win_loss_factors_json", type: "string" },
          {
            field: "competitive_positioning_note",
            as: "positioning_note",
            type: "string",
          },
          {
            field: "recommended_actions",
            as: "recommended_actions_json",
            type: "string",
          },
          {
            field: "confidence_level",
            as: "analysis_confidence",
            type: "string",
          },
        ],
      ),

      // 6. Competitive battle card update
      sheetsNode(
        "6",
        "Update competitive battle cards (Sheets)",
        1240,
        "BattleCards",
        '[["{deal_outcome}","{competitors_involved}","{deal_name}","{deal_amount}","{positioning_note}","{battle_card_updates_json}","{win_loss_factors_json}","{analysis_confidence}","=NOW()"]]',
      ),

      // 7. Insights distribution (Slack)
      slackNode(
        "7",
        "Slack: Share win/loss competitive insights",
        1480,
        "COMPETITIVE WIN/LOSS INSIGHT\\n\\nDeal: {deal_name} — *{deal_outcome}*\\nAmount: {deal_amount}\\nCompetitors: {competitors_involved}\\n\\n{positioning_note}\\n\\nRecommended actions: {recommended_actions_json}\\nConfidence: {analysis_confidence}",
      ),

      // 8. Monthly report (email)
      emailNode(
        "8",
        "Email: Send monthly win/loss report",
        1720,
        "{deal_owner_email}",
        "Win/Loss Intelligence: {deal_name} ({deal_outcome})",
        "Hi,\\n\\nHere is the competitive intelligence summary for the recently closed deal:\\n\\nDeal: {deal_name}\\nOutcome: {deal_outcome}\\nCompetitors: {competitors_involved}\\n\\nKey insights: {positioning_note}\\n\\nBattle card updates have been logged. Full analysis available in the competitive intelligence dashboard.\\n\\nBest,\\nCompetitive Intelligence Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 934. COMPETITOR COMPARISON BOT (8 nodes)
  // ===================================================================

  "Competitor Comparison Bot": {
    summary:
      "Receives comparison requests, parses with AI, fetches competitive matrix from Sheets, scrapes recent competitor updates via SerpAPI, generates balanced comparisons, tailors for audience, checks confidence, and delivers via Slack.",
    nodes: [
      trigger("Webhook: Comparison request received", "webhook_app", {
        source: "Slack / Internal Tools / Sales Portal",
        event: "comparison.requested",
        howItStarts:
          "A sales rep or team member requests a competitor comparison through Slack command, internal portal, or API — specifying the competitor and use case",
        setup:
          "Configure Slack slash command or internal tool to POST comparison requests. Maintain competitive matrix in Google Sheets. Set up SerpAPI for real-time competitor data.",
        inputFields: [],
      }),

      // 2. AI request parsing
      aiNode(
        "2",
        "AI: Parse comparison request",
        280,
        "You parse competitor comparison requests regardless of format. Extract the competitor name, comparison context, audience type, and specific areas of interest. Return JSON.",
        "Parse this comparison request: {webhook_payload}. Return competitor_name (string), comparison_context (what specifically to compare), audience_type (sales/executive/technical/customer), specific_areas (comma-separated areas like pricing,features,support), requester_name (string), requester_channel (string), urgency (high/normal/low).",
        [
          { field: "competitor_name", as: "competitor_name", type: "string" },
          {
            field: "comparison_context",
            as: "comparison_context",
            type: "string",
          },
          { field: "audience_type", as: "audience_type", type: "string" },
          { field: "specific_areas", as: "comparison_areas", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          {
            field: "requester_channel",
            as: "requester_channel",
            type: "string",
          },
          { field: "urgency", as: "request_urgency", type: "string" },
        ],
      ),

      // 3. Competitive matrix fetch (Sheets)
      n("3", "hitApi", "Fetch competitive matrix from Sheets", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{competitive_matrix_sheet_id}/values/CompMatrix!A1:Z100",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "values",
              customName: "competitive_matrix_json",
              type: "string",
            },
            {
              name: "values.length",
              customName: "matrix_rows",
              type: "number",
            },
          ],
        },
      }),

      // 4. Recent competitor updates scrape (SerpAPI)
      serpApiNode(
        "4",
        "SerpAPI: Fetch recent competitor updates",
        760,
        "{competitor_name} product update OR pricing change OR new feature 2024",
        [
          {
            name: "organic_results",
            customName: "recent_updates_json",
            type: "string",
          },
          {
            name: "organic_results.length",
            customName: "update_results_count",
            type: "number",
          },
        ],
      ),

      // 5. AI balanced comparison
      aiNode(
        "5",
        "AI: Generate balanced competitor comparison",
        1000,
        "You are a competitive intelligence analyst creating balanced, fact-based comparisons. Be honest about where competitors have advantages. Never make claims you cannot substantiate. Return JSON.",
        "Generate a competitor comparison. Competitor: {competitor_name}. Context: {comparison_context}. Areas: {comparison_areas}. Our matrix data: {competitive_matrix_json}. Recent updates: {recent_updates_json}. Return comparison_summary (balanced 300-word analysis), our_advantages (array), their_advantages (array), feature_comparison_table (markdown table), pricing_comparison (text), overall_positioning (text), confidence_score (1-10), data_freshness_note (text about data recency).",
        [
          {
            field: "comparison_summary",
            as: "comparison_summary",
            type: "string",
          },
          {
            field: "our_advantages",
            as: "our_advantages_json",
            type: "string",
          },
          {
            field: "their_advantages",
            as: "their_advantages_json",
            type: "string",
          },
          {
            field: "feature_comparison_table",
            as: "comparison_table",
            type: "string",
          },
          {
            field: "pricing_comparison",
            as: "pricing_comparison",
            type: "string",
          },
          {
            field: "overall_positioning",
            as: "positioning_text",
            type: "string",
          },
          {
            field: "confidence_score",
            as: "comparison_confidence",
            type: "number",
          },
          {
            field: "data_freshness_note",
            as: "freshness_note",
            type: "string",
          },
        ],
      ),

      // 6. Audience tailoring (runCode)
      n("6", "runCode", "Tailor comparison for target audience", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Tailor the comparison output for the target audience
const audience = vars.audience_type || "sales";
const summary = vars.comparison_summary || "";
const ourAdvantages = vars.our_advantages_json || "[]";
const theirAdvantages = vars.their_advantages_json || "[]";
const table = vars.comparison_table || "";
const pricing = vars.pricing_comparison || "";
const positioning = vars.positioning_text || "";
const confidence = parseInt(vars.comparison_confidence) || 5;
let tailoredOutput = "";
if (audience === "executive") {
  tailoredOutput = "EXECUTIVE SUMMARY\\n" + positioning + "\\n\\nKey differentiators: " + ourAdvantages + "\\n\\nRisks: " + theirAdvantages;
} else if (audience === "technical") {
  tailoredOutput = "TECHNICAL COMPARISON\\n" + table + "\\n\\nDetailed analysis: " + summary;
} else if (audience === "customer") {
  tailoredOutput = "SOLUTION COMPARISON\\n" + summary + "\\n\\nWhy we are the right fit: " + ourAdvantages;
} else {
  tailoredOutput = "SALES BATTLE CARD\\n" + summary + "\\n\\n" + table + "\\n\\nPricing: " + pricing + "\\n\\nOur strengths: " + ourAdvantages + "\\n\\nWatch out for: " + theirAdvantages;
}
return {
  tailored_comparison: tailoredOutput,
  audience_format: audience,
  confidence_sufficient: confidence >= 6 ? "yes" : "no",
  comparison_length: tailoredOutput.length
};`,
          outputVariables: [
            { name: "tailored_comparison", type: "string" },
            { name: "audience_format", type: "string" },
            { name: "confidence_sufficient", type: "string" },
            { name: "comparison_length", type: "number" },
          ],
        },
      }),

      // 7. Confidence check condition
      n("7", "condition", "Is comparison confidence sufficient?", 1480, {
        conditionData: [
          {
            variable: "confidence_sufficient",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Comparison delivery (Slack)
      slackNode(
        "8",
        "Slack: Deliver competitor comparison",
        1720,
        "COMPETITOR COMPARISON: {competitor_name}\\nRequested by: {requester_name} ({audience_format} format)\\n\\n{tailored_comparison}\\n\\nConfidence: {comparison_confidence}/10 | {freshness_note}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // confidence sufficient -> deliver
      e("7", "8", "output_2"), // low confidence -> deliver with warning
    ],
  },

  // ===================================================================
  // 935. COMPETITOR HOOK ANALYSIS & COUNTER (8 nodes)
  // ===================================================================

  "Competitor Hook Analysis & Counter": {
    summary:
      "Scans competitor video content on schedule, fetches competitor videos, analyzes hooks with AI, identifies patterns and trends, generates counter-hook strategies, drafts counter-hook scripts, routes through creative review, and publishes competitive briefs to Sheets.",
    nodes: [
      trigger("Schedule: Competitor content hook scan", "schedule", {
        schedule: "0 9 * * 3 (every Wednesday at 9 AM)",
        howItStarts:
          "A weekly schedule scans competitor video content (YouTube, TikTok, ads) for high-performing hooks and opening sequences to develop counter-strategies",
        setup:
          "Configure YouTube Data API and social media monitoring credentials. List competitor channels and accounts to monitor. Set up creative team Slack channel for brief distribution.",
        inputFields: [],
      }),

      // 2. Competitor video fetch
      n("2", "hitApi", "Fetch competitor video content data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.googleapis.com/youtube/v3/search?part=snippet&channelId={competitor_channel_id}&type=video&order=viewCount&maxResults=20&publishedAfter={last_scan_date}&key={youtube_api_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "items",
              customName: "competitor_videos_json",
              type: "string",
            },
            {
              name: "pageInfo.totalResults",
              customName: "total_videos_found",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI hook analysis
      aiNode(
        "3",
        "AI: Analyze video hooks and opening sequences",
        520,
        "You are a video marketing strategist who reverse-engineers high-performing content hooks. Analyze opening sequences (first 5-10 seconds) for psychological triggers, pattern interrupts, and engagement techniques. Return JSON.",
        "Analyze hooks from these competitor videos: {competitor_videos_json}. Total videos: {total_videos_found}. For each video identify hook_type (question/statistic/story/controversy/promise/curiosity), hook_text (reconstructed opening), psychological_trigger (what makes it work), engagement_prediction (high/medium/low). Return hook_analyses (array), dominant_hook_type (most common), trending_patterns (array of emerging hook patterns), avg_engagement_prediction (string).",
        [
          { field: "hook_analyses", as: "hook_analyses_json", type: "string" },
          {
            field: "dominant_hook_type",
            as: "dominant_hook_type",
            type: "string",
          },
          {
            field: "trending_patterns",
            as: "trending_patterns_json",
            type: "string",
          },
          {
            field: "avg_engagement_prediction",
            as: "avg_engagement_prediction",
            type: "string",
          },
        ],
      ),

      // 4. Pattern identification + trending
      aiNode(
        "4",
        "AI: Identify hook patterns and trends",
        760,
        "You are a content strategist who identifies meta-patterns across competitor hook strategies. Find what is changing in competitor messaging approaches. Return JSON.",
        "Identify patterns across these hook analyses: {hook_analyses_json}. Dominant type: {dominant_hook_type}. Trending patterns: {trending_patterns_json}. Return pattern_summary (text overview), emerging_tactics (array of new approaches), declining_tactics (array of fading approaches), competitor_messaging_shift (text on strategic direction), opportunity_gaps (array of hook types competitors are NOT using), counter_strategy_priority (ranked list of what to counter first).",
        [
          { field: "pattern_summary", as: "pattern_summary", type: "string" },
          {
            field: "emerging_tactics",
            as: "emerging_tactics_json",
            type: "string",
          },
          {
            field: "opportunity_gaps",
            as: "opportunity_gaps_json",
            type: "string",
          },
          {
            field: "counter_strategy_priority",
            as: "counter_priority_json",
            type: "string",
          },
          {
            field: "competitor_messaging_shift",
            as: "messaging_shift",
            type: "string",
          },
        ],
      ),

      // 5. Counter-hook strategy generation
      aiNode(
        "5",
        "AI: Generate counter-hook strategies",
        1000,
        "You are a creative director who develops counter-positioning strategies. Create hooks that differentiate from competitors while leveraging audience awareness of competitor messaging. Return JSON.",
        "Generate counter-hook strategies. Competitor patterns: {pattern_summary}. Emerging tactics: {emerging_tactics_json}. Opportunity gaps: {opportunity_gaps_json}. Priority counters: {counter_priority_json}. Messaging shift: {messaging_shift}. Return counter_strategies (array of {hook_type, counter_approach, example_hook, differentiation_angle, target_emotion}), total_strategies (number), top_strategy_name (string), top_strategy_rationale (text).",
        [
          {
            field: "counter_strategies",
            as: "counter_strategies_json",
            type: "string",
          },
          { field: "total_strategies", as: "total_strategies", type: "number" },
          {
            field: "top_strategy_name",
            as: "top_strategy_name",
            type: "string",
          },
          {
            field: "top_strategy_rationale",
            as: "top_strategy_rationale",
            type: "string",
          },
        ],
      ),

      // 6. AI counter-hook scripts
      aiNode(
        "6",
        "AI: Write counter-hook video scripts",
        1240,
        "You are a video scriptwriter who creates compelling opening hooks. Write complete hook scripts (first 10 seconds) that counter competitor approaches while being authentic. Return JSON.",
        "Write counter-hook scripts based on these strategies: {counter_strategies_json}. Top strategy: {top_strategy_name}. For each strategy write hook_script (word-for-word opening 10 seconds), visual_direction (what to show), tone_note (delivery guidance), a_b_variant (alternative version). Return scripts (array), total_scripts (number), production_notes (text), estimated_production_time (string).",
        [
          { field: "scripts", as: "counter_scripts_json", type: "string" },
          { field: "total_scripts", as: "total_scripts", type: "number" },
          { field: "production_notes", as: "production_notes", type: "string" },
          {
            field: "estimated_production_time",
            as: "est_production_time",
            type: "string",
          },
        ],
      ),

      // 7. Creative review approval gate
      n(
        "7",
        "approvalGate",
        "Creative review: Approve counter-hook briefs",
        1480,
        {
          approvalGateDetails: {
            approvers: ["{creative_director_email}"],
            message:
              "Weekly competitive hook analysis complete.\\n\\nCompetitor shift: {messaging_shift}\\nTop counter-strategy: {top_strategy_name}\\nRationale: {top_strategy_rationale}\\nScripts generated: {total_scripts}\\nEst. production: {est_production_time}\\n\\nPlease review and approve for creative team distribution.",
            timeout: 48,
          },
        },
      ),

      // 8. Competitive brief publishing (Sheets)
      sheetsNode(
        "8",
        "Publish competitive hook brief (Sheets)",
        1720,
        "CompetitiveHookBriefs",
        '[["{scrape_timestamp}","{total_videos_found}","{dominant_hook_type}","{pattern_summary}","{top_strategy_name}","{top_strategy_rationale}","{total_scripts}","{counter_scripts_json}","{production_notes}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 936. COMPETITOR INTELLIGENCE AGENT (10 nodes)
  // ===================================================================

  "Competitor Intelligence Agent": {
    summary:
      "Runs daily multi-source monitoring across competitor websites, news/PR, social/reviews, and job postings. AI synthesizes intelligence, classifies impact, routes high-impact items through condition, updates battle cards, and distributes intelligence via email and Slack.",
    nodes: [
      trigger("Schedule: Daily competitive intelligence sweep", "schedule", {
        schedule: "0 6 * * * (daily at 6 AM)",
        howItStarts:
          "A daily schedule triggers comprehensive multi-source competitive intelligence gathering across websites, news, social media, reviews, and job postings",
        setup:
          "Configure competitor domain list, SerpAPI for news monitoring, social monitoring API credentials, and job board API access. Set up battle card Sheets and Slack intelligence channel.",
        inputFields: [],
      }),

      // 2. Website change monitoring
      n("2", "hitApi", "Monitor competitor website changes", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.visualping.io/v2/jobs/check",
          headers: [
            { key: "Authorization", value: "Bearer {visualping_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "urls", value: "{competitor_urls_json}" }],
          variables: [
            {
              name: "results",
              customName: "website_changes_json",
              type: "string",
            },
            {
              name: "results.length",
              customName: "website_changes_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. News/PR monitoring (SerpAPI)
      serpApiNode(
        "3",
        "SerpAPI: Monitor competitor news and PR",
        520,
        "{competitor_names} announcement OR launch OR funding OR acquisition",
        [
          {
            name: "organic_results",
            customName: "news_results_json",
            type: "string",
          },
          {
            name: "organic_results.length",
            customName: "news_results_count",
            type: "number",
          },
        ],
      ),

      // 4. Social/review monitoring
      n("4", "hitApi", "Monitor social mentions and reviews", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.mention.com/api/accounts/{mention_account_id}/alerts/{alert_id}/mentions?limit=50&since_id={last_mention_id}",
          headers: [
            { key: "Authorization", value: "Bearer {mention_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "mentions",
              customName: "social_mentions_json",
              type: "string",
            },
            {
              name: "mentions.length",
              customName: "social_mentions_count",
              type: "number",
            },
          ],
        },
      }),

      // 5. Job posting monitoring
      n("5", "hitApi", "Monitor competitor job postings", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?engine=google_jobs&q={competitor_names}+jobs&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "jobs_results",
              customName: "job_postings_json",
              type: "string",
            },
            {
              name: "jobs_results.length",
              customName: "job_postings_count",
              type: "number",
            },
          ],
        },
      }),

      // 6. AI intelligence synthesis
      aiNode(
        "6",
        "AI: Synthesize competitive intelligence",
        1240,
        "You are a competitive intelligence analyst synthesizing multi-source data into actionable intelligence. Identify strategic implications, market shifts, and competitive threats. Return JSON.",
        "Synthesize competitive intelligence. Website changes: {website_changes_json} ({website_changes_count}). News/PR: {news_results_json} ({news_results_count}). Social mentions: {social_mentions_json} ({social_mentions_count}). Job postings: {job_postings_json} ({job_postings_count}). Return intelligence_summary (executive-level 200 words), key_signals (array of {signal, source, implication, impact_level}), strategic_implications (array), market_shifts_detected (array), threat_level (critical/high/medium/low), total_signals (number), has_high_impact (yes/no).",
        [
          {
            field: "intelligence_summary",
            as: "intel_summary",
            type: "string",
          },
          { field: "key_signals", as: "key_signals_json", type: "string" },
          {
            field: "strategic_implications",
            as: "strategic_implications_json",
            type: "string",
          },
          { field: "threat_level", as: "threat_level", type: "string" },
          { field: "total_signals", as: "total_signals", type: "number" },
          { field: "has_high_impact", as: "has_high_impact", type: "string" },
        ],
      ),

      // 7. Impact classification
      n("7", "runCode", "Classify intelligence impact level", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify the overall impact and determine routing
const threat = vars.threat_level || "low";
const signals = parseInt(vars.total_signals) || 0;
const hasHighImpact = vars.has_high_impact === "yes";
const websiteChanges = parseInt(vars.website_changes_count) || 0;
const newsCount = parseInt(vars.news_results_count) || 0;
const impactScore = (threat === "critical" ? 10 : threat === "high" ? 7 : threat === "medium" ? 4 : 1) + (signals * 0.5) + (websiteChanges * 2) + (newsCount * 1.5);
const isHighImpact = impactScore >= 8 || hasHighImpact || threat === "critical";
return {
  impact_score: Math.round(impactScore * 10) / 10,
  is_high_impact: isHighImpact ? "yes" : "no",
  urgency_label: isHighImpact ? "URGENT" : "ROUTINE",
  routing_priority: isHighImpact ? "immediate" : "daily_digest",
  total_data_points: websiteChanges + newsCount + signals
};`,
          outputVariables: [
            { name: "impact_score", type: "number" },
            { name: "is_high_impact", type: "string" },
            { name: "urgency_label", type: "string" },
            { name: "routing_priority", type: "string" },
            { name: "total_data_points", type: "number" },
          ],
        },
      }),

      // 8. High-impact condition
      n("8", "condition", "Is this high-impact intelligence?", 1720, {
        conditionData: [
          {
            variable: "is_high_impact",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 9. Automated battle card updates
      sheetsNode(
        "9",
        "Update battle cards with new intelligence (Sheets)",
        1960,
        "BattleCards",
        '[["{urgency_label}","{threat_level}","{impact_score}","{intel_summary}","{key_signals_json}","{strategic_implications_json}","{total_data_points}","=NOW()"]]',
      ),

      // 10. Intelligence product distribution (email + Slack)
      slackNode(
        "10",
        "Distribute competitive intelligence (Slack + Email)",
        2200,
        "{urgency_label} COMPETITIVE INTELLIGENCE REPORT\\n\\nThreat level: {threat_level} | Impact score: {impact_score}\\n\\n{intel_summary}\\n\\nKey signals: {total_signals} across {total_data_points} data points\\nStrategic implications: {strategic_implications_json}\\n\\nBattle cards updated. Full report delivered via email.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"), // high impact -> update battle cards immediately
      e("8", "10", "output_2"), // routine -> skip to distribution
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 937. COMPETITOR MENTION ALERT (7 nodes)
  // ===================================================================

  "Competitor Mention Alert": {
    summary:
      "Monitors real-time competitor mentions, classifies context with AI, calculates alert priority via runCode, routes by priority condition, generates AI response suggestions, dispatches to Slack, and sends daily mention digests via email.",
    nodes: [
      trigger("Webhook: Real-time competitor mention detected", "webhook_app", {
        source: "Mention / Brand24 / Google Alerts",
        event: "mention.detected",
        howItStarts:
          "A real-time monitoring tool detects a mention of your brand alongside a competitor, triggering immediate classification and routing",
        setup:
          "Configure Mention, Brand24, or Google Alerts to fire webhooks on competitor co-mentions. Set priority thresholds and Slack channels for different alert levels.",
        inputFields: [],
      }),

      // 2. AI mention context classification
      aiNode(
        "2",
        "AI: Classify mention context and sentiment",
        280,
        "You classify competitive mentions by context, sentiment, reach, and urgency. Identify whether the mention is a comparison, complaint, recommendation, or news. Return JSON.",
        "Classify this competitor mention: {webhook_payload}. Return mention_text (the actual mention), mention_source (platform/site), mention_author (who posted), author_reach (estimated audience size), mention_type (comparison/complaint/recommendation/news/question), sentiment (positive/negative/neutral), competitor_mentioned (which competitor), our_brand_mentioned (yes/no), requires_response (yes/no), mention_url (link), mention_date (ISO date).",
        [
          { field: "mention_text", as: "mention_text", type: "string" },
          { field: "mention_source", as: "mention_source", type: "string" },
          { field: "mention_author", as: "mention_author", type: "string" },
          { field: "author_reach", as: "author_reach", type: "number" },
          { field: "mention_type", as: "mention_type", type: "string" },
          { field: "sentiment", as: "mention_sentiment", type: "string" },
          {
            field: "competitor_mentioned",
            as: "competitor_mentioned",
            type: "string",
          },
          {
            field: "requires_response",
            as: "requires_response",
            type: "string",
          },
          { field: "mention_url", as: "mention_url", type: "string" },
        ],
      ),

      // 3. Alert priority calculation (runCode)
      n("3", "runCode", "Calculate alert priority score", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate priority based on sentiment, reach, and mention type
const sentiment = vars.mention_sentiment || "neutral";
const reach = parseInt(vars.author_reach) || 0;
const type = vars.mention_type || "news";
const requiresResponse = vars.requires_response === "yes";
let score = 0;
// Sentiment scoring
if (sentiment === "negative") score += 30;
else if (sentiment === "positive") score += 10;
else score += 5;
// Reach scoring
if (reach > 100000) score += 40;
else if (reach > 10000) score += 25;
else if (reach > 1000) score += 15;
else score += 5;
// Type scoring
const typeScores = { comparison: 25, complaint: 30, recommendation: 20, question: 15, news: 10 };
score += typeScores[type] || 10;
// Response requirement
if (requiresResponse) score += 20;
const priority = score >= 70 ? "critical" : score >= 40 ? "high" : score >= 20 ? "medium" : "low";
const isHighPriority = priority === "critical" || priority === "high";
return {
  priority_score: score,
  priority_level: priority,
  is_high_priority: isHighPriority ? "yes" : "no",
  response_urgency: isHighPriority ? "immediate" : "next_business_day",
  scoring_breakdown: "sentiment:" + sentiment + "=" + (sentiment === "negative" ? 30 : 10) + " reach:" + reach + " type:" + type
};`,
          outputVariables: [
            { name: "priority_score", type: "number" },
            { name: "priority_level", type: "string" },
            { name: "is_high_priority", type: "string" },
            { name: "response_urgency", type: "string" },
            { name: "scoring_breakdown", type: "string" },
          ],
        },
      }),

      // 4. Priority routing condition
      n("4", "condition", "Is this a high-priority mention?", 760, {
        conditionData: [
          {
            variable: "is_high_priority",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI response suggestions
      aiNode(
        "5",
        "AI: Generate response suggestions",
        1000,
        "You are a brand strategist who crafts appropriate responses to competitive mentions. Be professional, factual, and never disparaging. Return JSON.",
        "Generate response suggestions for this mention. Text: {mention_text}. Source: {mention_source}. Author: {mention_author}. Type: {mention_type}. Sentiment: {mention_sentiment}. Competitor: {competitor_mentioned}. Priority: {priority_level}. Return suggested_response (ready-to-post response), response_tone (professional/empathetic/educational), talking_points (array of 3-5 key points), do_not_say (array of things to avoid), escalation_needed (yes/no).",
        [
          {
            field: "suggested_response",
            as: "suggested_response",
            type: "string",
          },
          { field: "response_tone", as: "response_tone", type: "string" },
          {
            field: "talking_points",
            as: "talking_points_json",
            type: "string",
          },
          { field: "do_not_say", as: "do_not_say_json", type: "string" },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "string",
          },
        ],
      ),

      // 6. Dispatch alerts (Slack)
      slackNode(
        "6",
        "Slack: Dispatch competitor mention alert",
        1240,
        "{priority_level} COMPETITOR MENTION ALERT\\n\\nCompetitor: {competitor_mentioned} | Source: {mention_source}\\nType: {mention_type} | Sentiment: {mention_sentiment}\\nAuthor reach: {author_reach}\\n\\nMention: {mention_text}\\nURL: {mention_url}\\n\\nSuggested response ({response_tone}): {suggested_response}\\nTalking points: {talking_points_json}\\n\\nPriority: {priority_score}/100 | Urgency: {response_urgency}",
      ),

      // 7. Daily mention digest (email)
      emailNode(
        "7",
        "Email: Daily competitor mention digest",
        1480,
        "{marketing_team_email}",
        "Competitor Mention Alert: {competitor_mentioned} on {mention_source} ({priority_level})",
        "Competitor Mention Detected\\n\\nCompetitor: {competitor_mentioned}\\nSource: {mention_source}\\nType: {mention_type}\\nSentiment: {mention_sentiment}\\nPriority: {priority_level} (score: {priority_score})\\n\\nMention: {mention_text}\\nURL: {mention_url}\\n\\nSuggested response: {suggested_response}\\n\\nThis alert was auto-classified and prioritized by the Competitor Mention Alert workflow.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high priority -> generate response
      e("4", "7", "output_2"), // low priority -> skip to daily digest
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 938. CONTENT CALENDAR PLANNER (7 nodes)
  // ===================================================================

  "Content Calendar Planner": {
    summary:
      "Receives a planning request, fetches historical performance from GA4, generates an AI content calendar, balances the content mix via runCode, generates briefs per item, creates Asana tasks, and delivers the calendar via Sheets and Slack.",
    nodes: [
      trigger("Webhook: Content calendar planning request", "webhook_app", {
        source: "Slack / Marketing Portal",
        event: "content.calendar.requested",
        howItStarts:
          "A content manager requests a new content calendar for an upcoming period, specifying timeframe, themes, and channel priorities",
        setup:
          "Connect Google Analytics 4 for performance data. Configure Asana project for content tasks. Set up Google Sheets for calendar output and Slack for delivery.",
        inputFields: [],
      }),

      // 2. Historical performance fetch (GA4)
      aiNode(
        "2",
        "AI: Parse planning request and set parameters",
        280,
        "You parse content calendar planning requests regardless of format. Extract timeframe, themes, channel preferences, and goals. Return JSON.",
        "Parse this content calendar request: {webhook_payload}. Return planning_period (e.g. Q2 2024), start_date (ISO), end_date (ISO), themes (comma-separated), priority_channels (comma-separated), content_goal (traffic/leads/engagement/awareness), target_pieces_per_week (number), requester_name (string).",
        [
          { field: "planning_period", as: "planning_period", type: "string" },
          { field: "start_date", as: "calendar_start_date", type: "string" },
          { field: "end_date", as: "calendar_end_date", type: "string" },
          { field: "themes", as: "content_themes", type: "string" },
          {
            field: "priority_channels",
            as: "priority_channels",
            type: "string",
          },
          { field: "content_goal", as: "content_goal", type: "string" },
          {
            field: "target_pieces_per_week",
            as: "weekly_target",
            type: "number",
          },
          { field: "requester_name", as: "requester_name", type: "string" },
        ],
      ),

      // 3. AI content calendar generation
      aiNode(
        "3",
        "AI: Generate content calendar",
        520,
        "You are a content strategist who builds data-driven editorial calendars. Balance content types, channels, and themes across the planning period. Return JSON.",
        "Generate a content calendar for {planning_period} ({calendar_start_date} to {calendar_end_date}). Themes: {content_themes}. Channels: {priority_channels}. Goal: {content_goal}. Weekly target: {weekly_target} pieces. Return calendar_items (array of {week_number, publish_date, title, content_type, channel, theme, target_keyword, estimated_effort_hours, priority}), total_items (number), content_type_distribution (object), channel_distribution (object), calendar_summary (text overview).",
        [
          {
            field: "calendar_items",
            as: "calendar_items_json",
            type: "string",
          },
          { field: "total_items", as: "total_calendar_items", type: "number" },
          {
            field: "content_type_distribution",
            as: "type_distribution_json",
            type: "string",
          },
          {
            field: "channel_distribution",
            as: "channel_distribution_json",
            type: "string",
          },
          { field: "calendar_summary", as: "calendar_summary", type: "string" },
        ],
      ),

      // 4. Content mix balancing (runCode)
      n("4", "runCode", "Balance content mix and validate calendar", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Balance content mix and validate distribution
const items = JSON.parse(vars.calendar_items_json || "[]");
const total = items.length;
const typeCount = {};
const channelCount = {};
const weekCount = {};
for (const item of items) {
  typeCount[item.content_type] = (typeCount[item.content_type] || 0) + 1;
  channelCount[item.channel] = (channelCount[item.channel] || 0) + 1;
  weekCount[item.week_number] = (weekCount[item.week_number] || 0) + 1;
}
const maxWeekLoad = Math.max(...Object.values(weekCount));
const minWeekLoad = Math.min(...Object.values(weekCount));
const isBalanced = (maxWeekLoad - minWeekLoad) <= 2;
const totalHours = items.reduce((sum, i) => sum + (i.estimated_effort_hours || 4), 0);
return {
  calendar_balanced: isBalanced ? "yes" : "needs_adjustment",
  total_items_validated: total,
  total_effort_hours: totalHours,
  type_distribution: JSON.stringify(typeCount),
  channel_distribution: JSON.stringify(channelCount),
  busiest_week: maxWeekLoad,
  lightest_week: minWeekLoad,
  avg_weekly_load: Math.round(total / Object.keys(weekCount).length * 10) / 10
};`,
          outputVariables: [
            { name: "calendar_balanced", type: "string" },
            { name: "total_items_validated", type: "number" },
            { name: "total_effort_hours", type: "number" },
            { name: "type_distribution", type: "string" },
            { name: "channel_distribution", type: "string" },
            { name: "busiest_week", type: "number" },
            { name: "lightest_week", type: "number" },
            { name: "avg_weekly_load", type: "number" },
          ],
        },
      }),

      // 5. Brief generation per item
      aiNode(
        "5",
        "AI: Generate content briefs for calendar items",
        1000,
        "You are a content strategist writing detailed briefs for each calendar item. Include target audience, key messages, SEO guidance, and success metrics. Return JSON.",
        "Generate briefs for the top 5 priority items from the calendar: {calendar_items_json}. Goal: {content_goal}. Themes: {content_themes}. For each return brief_title, target_audience, key_messages (array), seo_keywords (array), outline (array of sections), success_metric, estimated_word_count. Return briefs (array), total_briefs (number).",
        [
          { field: "briefs", as: "content_briefs_json", type: "string" },
          { field: "total_briefs", as: "total_briefs", type: "number" },
        ],
      ),

      // 6. Asana task creation
      asanaNode(
        "6",
        "Create content tasks in Asana",
        1240,
        "{planning_period} Content Calendar — {total_calendar_items} items",
        "Content calendar for {planning_period}. {calendar_summary}. Total items: {total_calendar_items}. Effort: {total_effort_hours}h. Distribution: {type_distribution}. See Google Sheets for full calendar and briefs.",
      ),

      // 7. Calendar delivery (Sheets + Slack)
      slackNode(
        "7",
        "Deliver content calendar (Sheets + Slack)",
        1480,
        "CONTENT CALENDAR READY: {planning_period}\\n\\nRequested by: {requester_name}\\nTotal items: {total_calendar_items}\\nTotal effort: {total_effort_hours} hours\\nAvg weekly load: {avg_weekly_load} items/week\\nBalance: {calendar_balanced}\\n\\n{calendar_summary}\\n\\nAsana tasks created: {asana_task_url}\\nFull calendar and briefs in Google Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 939. CONTENT GAP ANALYZER (8 nodes)
  // ===================================================================

  "Content Gap Analyzer": {
    summary:
      "Triggers on analysis request, crawls own content inventory, analyzes competitor content via SerpAPI, fetches search demand data, identifies gaps with AI, prioritizes opportunities via runCode, generates content recommendations, and delivers a gap analysis report via email.",
    nodes: [
      trigger("Webhook: Content gap analysis requested", "webhook_app", {
        source: "Marketing Portal / Slack",
        event: "content.gap.analysis.requested",
        howItStarts:
          "A marketer requests a content gap analysis for a specific topic area or product category, comparing our content coverage against competitors and search demand",
        setup:
          "Configure site crawl access or content inventory API. Set up SerpAPI for competitor analysis and search volume data. Provide analysis report email distribution list.",
        inputFields: [],
      }),

      // 2. Own content inventory crawl
      aiNode(
        "2",
        "AI: Parse request and map content scope",
        280,
        "You parse content gap analysis requests regardless of format. Extract the topic area, target audience, and competitive context. Return JSON.",
        "Parse this content gap analysis request: {webhook_payload}. Return topic_area (specific topic or product category), target_audience (who the content is for), competitor_domains (comma-separated competitor sites to analyze), our_domain (our website domain), analysis_depth (comprehensive/focused), content_types_to_analyze (blog/landing/video/etc, comma-separated), requester_email (string).",
        [
          { field: "topic_area", as: "topic_area", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          {
            field: "competitor_domains",
            as: "competitor_domains",
            type: "string",
          },
          { field: "our_domain", as: "our_domain", type: "string" },
          { field: "analysis_depth", as: "analysis_depth", type: "string" },
          {
            field: "content_types_to_analyze",
            as: "content_types",
            type: "string",
          },
          { field: "requester_email", as: "requester_email", type: "string" },
        ],
      ),

      // 3. Competitor content analysis (SerpAPI)
      serpApiNode(
        "3",
        "SerpAPI: Analyze competitor content coverage",
        520,
        "site:{competitor_domains} {topic_area}",
        [
          {
            name: "organic_results",
            customName: "competitor_content_json",
            type: "string",
          },
          {
            name: "organic_results.length",
            customName: "competitor_content_count",
            type: "number",
          },
          {
            name: "search_information.total_results",
            customName: "competitor_total_results",
            type: "number",
          },
        ],
      ),

      // 4. Search demand data fetch
      serpApiNode(
        "4",
        "SerpAPI: Fetch search demand and related queries",
        760,
        "{topic_area} {target_audience}",
        [
          {
            name: "related_searches",
            customName: "related_searches_json",
            type: "string",
          },
          {
            name: "related_questions",
            customName: "related_questions_json",
            type: "string",
          },
          {
            name: "organic_results",
            customName: "serp_landscape_json",
            type: "string",
          },
        ],
      ),

      // 5. AI gap identification
      aiNode(
        "5",
        "AI: Identify content gaps and opportunities",
        1000,
        "You are a content strategist who identifies gaps between existing content, competitor coverage, and search demand. Be specific about what content is missing and why it matters. Return JSON.",
        "Identify content gaps. Topic: {topic_area}. Audience: {target_audience}. Our domain: {our_domain}. Competitor content ({competitor_content_count} pages): {competitor_content_json}. Related searches: {related_searches_json}. Related questions: {related_questions_json}. SERP landscape: {serp_landscape_json}. Return gaps (array of {topic, gap_type, competitor_coverage, search_demand, our_coverage, opportunity_score}), total_gaps (number), critical_gaps (array of highest priority), topic_clusters_missing (array), content_format_gaps (array), competitive_advantage_opportunities (array).",
        [
          { field: "gaps", as: "content_gaps_json", type: "string" },
          { field: "total_gaps", as: "total_gaps", type: "number" },
          { field: "critical_gaps", as: "critical_gaps_json", type: "string" },
          {
            field: "topic_clusters_missing",
            as: "missing_clusters_json",
            type: "string",
          },
          {
            field: "content_format_gaps",
            as: "format_gaps_json",
            type: "string",
          },
          {
            field: "competitive_advantage_opportunities",
            as: "advantage_opportunities_json",
            type: "string",
          },
        ],
      ),

      // 6. Opportunity prioritization (runCode)
      n("6", "runCode", "Prioritize content gap opportunities", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Prioritize gaps by impact potential
const gaps = JSON.parse(vars.content_gaps_json || "[]");
const criticalGaps = JSON.parse(vars.critical_gaps_json || "[]");
const sorted = gaps.sort((a, b) => (b.opportunity_score || 0) - (a.opportunity_score || 0));
const topOpportunities = sorted.slice(0, 10);
const quickWins = sorted.filter(g => g.gap_type === "thin_content" || g.gap_type === "outdated");
const newContent = sorted.filter(g => g.gap_type === "missing" || g.gap_type === "no_coverage");
const totalEstEffort = topOpportunities.reduce((s, g) => s + (g.gap_type === "missing" ? 8 : 4), 0);
return {
  prioritized_gaps_json: JSON.stringify(topOpportunities),
  total_prioritized: topOpportunities.length,
  quick_wins_count: quickWins.length,
  new_content_needed: newContent.length,
  estimated_effort_hours: totalEstEffort,
  top_opportunity_topic: topOpportunities[0] ? topOpportunities[0].topic : "none",
  top_opportunity_score: topOpportunities[0] ? topOpportunities[0].opportunity_score : 0
};`,
          outputVariables: [
            { name: "prioritized_gaps_json", type: "string" },
            { name: "total_prioritized", type: "number" },
            { name: "quick_wins_count", type: "number" },
            { name: "new_content_needed", type: "number" },
            { name: "estimated_effort_hours", type: "number" },
            { name: "top_opportunity_topic", type: "string" },
            { name: "top_opportunity_score", type: "number" },
          ],
        },
      }),

      // 7. Content recommendations
      aiNode(
        "7",
        "AI: Generate content recommendations",
        1480,
        "You are a content strategist turning gap analysis into actionable content recommendations. Each recommendation should include title, format, outline, and expected impact. Return JSON.",
        "Generate content recommendations from these prioritized gaps: {prioritized_gaps_json}. Topic area: {topic_area}. Audience: {target_audience}. Missing clusters: {missing_clusters_json}. Format gaps: {format_gaps_json}. For each gap return recommended_title, content_format, target_keyword, outline (array), expected_traffic_impact, priority_rank. Return recommendations (array), executive_summary (text), implementation_timeline (text).",
        [
          {
            field: "recommendations",
            as: "recommendations_json",
            type: "string",
          },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          {
            field: "implementation_timeline",
            as: "implementation_timeline",
            type: "string",
          },
        ],
      ),

      // 8. Gap analysis report (email)
      emailNode(
        "8",
        "Email: Deliver content gap analysis report",
        1720,
        "{requester_email}",
        "Content Gap Analysis: {topic_area} — {total_gaps} gaps identified",
        "Content Gap Analysis Report\\n\\nTopic: {topic_area}\\nAudience: {target_audience}\\n\\n{executive_summary}\\n\\nKey Findings:\\n- Total gaps identified: {total_gaps}\\n- Top prioritized: {total_prioritized}\\n- Quick wins: {quick_wins_count}\\n- New content needed: {new_content_needed}\\n- Estimated effort: {estimated_effort_hours} hours\\n\\nTop opportunity: {top_opportunity_topic} (score: {top_opportunity_score})\\n\\nImplementation Timeline: {implementation_timeline}\\n\\nFull recommendations and detailed analysis attached.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 940. CONTENT LOCALIZATION WORKFLOW (9 nodes)
  // ===================================================================

  "Content Localization Workflow": {
    summary:
      "Triggers on localization flag, fetches source content and glossary, translates with AI, adapts visual and formatting via runCode, runs AI quality assurance, routes QA issues through condition, sends to native speaker review, publishes to localized CMS, and reports status to Sheets.",
    nodes: [
      trigger("Webhook: Content flagged for localization", "webhook_app", {
        source: "CMS / Content Platform",
        event: "content.localization.requested",
        howItStarts:
          "A content item is flagged for localization in the CMS, specifying target languages and markets for translation and cultural adaptation",
        setup:
          "Configure CMS webhook for localization flags. Maintain translation glossary in Sheets. Set up native speaker reviewer contacts per language. Configure localized CMS publishing endpoints.",
        inputFields: [],
      }),

      // 2. Source content + glossary fetch
      aiNode(
        "2",
        "AI: Extract localization request details",
        280,
        "You parse content localization requests regardless of format. Extract source content, target languages, and context. Return JSON.",
        "Parse this localization request: {webhook_payload}. Return content_id (string), content_title (string), content_body (full source text), source_language (e.g. en), target_languages (comma-separated e.g. es,fr,de), content_type (blog/landing/email/ui), priority (high/normal/low), requester_email (string), glossary_sheet_id (string if provided).",
        [
          { field: "content_id", as: "content_id", type: "string" },
          { field: "content_title", as: "content_title", type: "string" },
          { field: "content_body", as: "source_content", type: "string" },
          { field: "source_language", as: "source_language", type: "string" },
          { field: "target_languages", as: "target_languages", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "priority", as: "localization_priority", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
        ],
      ),

      // 3. AI translation
      aiNode(
        "3",
        "AI: Translate content to target languages",
        520,
        "You are a professional translator who produces natural, culturally adapted translations. Maintain brand voice while adapting idioms and cultural references. Use the glossary for consistent terminology. Return JSON.",
        "Translate this content. Source ({source_language}): {source_content}. Title: {content_title}. Target languages: {target_languages}. Content type: {content_type}. For each target language return language_code, translated_title, translated_body, cultural_adaptations_made (array of changes), confidence_score (1-10). Return translations (array), total_translations (number), primary_translation_lang (first target language), primary_translated_title (string), primary_translated_body (string).",
        [
          { field: "translations", as: "translations_json", type: "string" },
          {
            field: "total_translations",
            as: "total_translations",
            type: "number",
          },
          {
            field: "primary_translation_lang",
            as: "primary_lang",
            type: "string",
          },
          {
            field: "primary_translated_title",
            as: "primary_translated_title",
            type: "string",
          },
          {
            field: "primary_translated_body",
            as: "primary_translated_body",
            type: "string",
          },
        ],
      ),

      // 4. Visual/formatting localization (runCode)
      n("4", "runCode", "Adapt formatting for target locales", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Adapt formatting for different locales
const translations = JSON.parse(vars.translations_json || "[]");
const contentType = vars.content_type || "blog";
const adapted = translations.map(t => {
  const lang = t.language_code;
  let body = t.translated_body;
  // RTL language detection
  const rtlLangs = ["ar", "he", "fa", "ur"];
  const isRtl = rtlLangs.includes(lang);
  if (isRtl) body = '<div dir="rtl">' + body + '</div>';
  // Date format adaptation
  const useMDY = ["en-US"].includes(lang);
  const useDMY = ["en-GB", "fr", "de", "es", "it", "pt"].includes(lang);
  // Number format
  const useCommaDecimal = ["de", "fr", "es", "it", "pt", "nl"].includes(lang);
  return {
    ...t,
    adapted_body: body,
    is_rtl: isRtl,
    date_format: useMDY ? "MM/DD/YYYY" : "DD/MM/YYYY",
    decimal_separator: useCommaDecimal ? "," : ".",
    formatting_notes: (isRtl ? "RTL layout applied. " : "") + "Date/number formats localized."
  };
});
return {
  adapted_translations_json: JSON.stringify(adapted),
  total_adapted: adapted.length,
  rtl_languages_found: adapted.filter(a => a.is_rtl).length,
  formatting_complete: "yes"
};`,
          outputVariables: [
            { name: "adapted_translations_json", type: "string" },
            { name: "total_adapted", type: "number" },
            { name: "rtl_languages_found", type: "number" },
            { name: "formatting_complete", type: "string" },
          ],
        },
      }),

      // 5. AI quality assurance
      aiNode(
        "5",
        "AI: Quality assurance on translations",
        1000,
        "You are a translation quality assurance specialist. Check for grammar, consistency, cultural appropriateness, brand voice adherence, and technical accuracy. Return JSON.",
        "QA check these localized translations: {adapted_translations_json}. Original title: {content_title}. Source content: {source_content}. Content type: {content_type}. For each translation check grammar_score (1-10), consistency_score (1-10), cultural_appropriateness (1-10), overall_qa_score (1-10), issues_found (array). Return qa_results (array), overall_pass (yes/no), critical_issues_count (number), total_issues (number), qa_summary (text).",
        [
          { field: "qa_results", as: "qa_results_json", type: "string" },
          { field: "overall_pass", as: "qa_passed", type: "string" },
          {
            field: "critical_issues_count",
            as: "critical_qa_issues",
            type: "number",
          },
          { field: "total_issues", as: "total_qa_issues", type: "number" },
          { field: "qa_summary", as: "qa_summary", type: "string" },
        ],
      ),

      // 6. QA issues condition
      n("6", "condition", "Did translations pass QA?", 1240, {
        conditionData: [
          {
            variable: "qa_passed",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Native speaker review approval gate
      n(
        "7",
        "approvalGate",
        "Native speaker review: Approve translations",
        1480,
        {
          approvalGateDetails: {
            approvers: ["{native_reviewer_email}"],
            message:
              "Localized content ready for native speaker review.\\n\\nOriginal: {content_title}\\nLanguages: {target_languages}\\nTotal translations: {total_translations}\\nQA status: {qa_passed} ({total_qa_issues} issues)\\nQA summary: {qa_summary}\\n\\nPlease review for natural fluency and cultural accuracy.",
            timeout: 72,
          },
        },
      ),

      // 8. Localized CMS publishing
      n("8", "hitApi", "Publish localized content to CMS", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://{cms_domain}/api/content/{content_id}/translations",
          headers: [
            { key: "Authorization", value: "Bearer {cms_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "translations", value: "{adapted_translations_json}" },
            { key: "source_language", value: "{source_language}" },
            { key: "publish", value: "true" },
          ],
          variables: [
            {
              name: "published_count",
              customName: "published_count",
              type: "number",
            },
            {
              name: "published_urls",
              customName: "published_urls_json",
              type: "string",
            },
          ],
        },
      }),

      // 9. Localization status report (Sheets)
      sheetsNode(
        "9",
        "Log localization status (Sheets)",
        1960,
        "LocalizationLog",
        '[["{content_id}","{content_title}","{source_language}","{target_languages}","{total_translations}","{qa_passed}","{total_qa_issues}","{published_count}","{localization_priority}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // QA passed -> native review
      e("6", "3", "output_2"), // QA failed -> re-translate
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 941. CONTENT MODERATION AGENT (9 nodes)
  // ===================================================================

  "Content Moderation Agent": {
    summary:
      "Triggers on content submission, analyzes text with AI, analyzes images/video with AI, calculates moderation score via runCode, routes on moderation decision condition, auto-approves or rejects, queues for human moderator, handles appeals, and reports moderation metrics to Sheets.",
    nodes: [
      trigger("Webhook: Content submitted for moderation", "webhook_app", {
        source: "User Platform / CMS / Community",
        event: "content.submitted",
        howItStarts:
          "User-generated content is submitted through the platform (posts, comments, images, videos) and requires automated moderation before publication",
        setup:
          "Configure content submission webhook from your platform. Set moderation thresholds for auto-approve, human review, and auto-reject. Set up moderator queue and appeal process.",
        inputFields: [],
      }),

      // 2. AI text analysis
      aiNode(
        "2",
        "AI: Analyze text content for policy violations",
        280,
        "You are a content moderation specialist. Analyze text for hate speech, harassment, spam, misinformation, adult content, violence, and self-harm. Be thorough but avoid false positives. Return JSON.",
        "Moderate this submitted content: {webhook_payload}. Analyze text for policy violations. Return content_id (string), content_text (the submitted text), author_id (string), content_type (post/comment/review), text_toxicity_score (0-1), spam_score (0-1), hate_speech_detected (yes/no), harassment_detected (yes/no), adult_content_detected (yes/no), violence_detected (yes/no), misinformation_flags (array), overall_text_risk (high/medium/low/none), flagged_phrases (array).",
        [
          { field: "content_id", as: "content_id", type: "string" },
          { field: "content_text", as: "content_text", type: "string" },
          { field: "author_id", as: "author_id", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          {
            field: "text_toxicity_score",
            as: "toxicity_score",
            type: "number",
          },
          { field: "spam_score", as: "spam_score", type: "number" },
          { field: "overall_text_risk", as: "text_risk_level", type: "string" },
          {
            field: "flagged_phrases",
            as: "flagged_phrases_json",
            type: "string",
          },
        ],
      ),

      // 3. AI image/video analysis
      aiNode(
        "3",
        "AI: Analyze visual content for policy violations",
        520,
        "You analyze image and video content descriptions for policy violations including nudity, violence, copyright issues, and misleading visuals. Return JSON.",
        "Analyze visual content attached to submission {content_id}. Content text context: {content_text}. Author: {author_id}. Check for visual_nudity_score (0-1), visual_violence_score (0-1), copyright_risk (yes/no), misleading_visual (yes/no), overall_visual_risk (high/medium/low/none), visual_flags (array of specific issues).",
        [
          { field: "visual_nudity_score", as: "nudity_score", type: "number" },
          {
            field: "visual_violence_score",
            as: "visual_violence_score",
            type: "number",
          },
          { field: "copyright_risk", as: "copyright_risk", type: "string" },
          {
            field: "overall_visual_risk",
            as: "visual_risk_level",
            type: "string",
          },
          { field: "visual_flags", as: "visual_flags_json", type: "string" },
        ],
      ),

      // 4. Moderation score calculation (runCode)
      n("4", "runCode", "Calculate composite moderation score", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate composite moderation score and decision
const toxicity = parseFloat(vars.toxicity_score) || 0;
const spam = parseFloat(vars.spam_score) || 0;
const nudity = parseFloat(vars.nudity_score) || 0;
const violence = parseFloat(vars.visual_violence_score) || 0;
const textRisk = vars.text_risk_level || "none";
const visualRisk = vars.visual_risk_level || "none";
const copyrightRisk = vars.copyright_risk === "yes";
// Weighted composite score (0-100, higher = more risky)
const compositeScore = Math.round(
  (toxicity * 30) + (spam * 20) + (nudity * 25) + (violence * 25)
) * 100 / 100;
// Decision thresholds
let decision;
if (compositeScore >= 70 || textRisk === "high" || visualRisk === "high" || copyrightRisk) {
  decision = "reject";
} else if (compositeScore >= 30 || textRisk === "medium" || visualRisk === "medium") {
  decision = "human_review";
} else {
  decision = "approve";
}
return {
  composite_score: compositeScore,
  moderation_decision: decision,
  is_auto_actionable: decision !== "human_review" ? "yes" : "no",
  risk_summary: "Text:" + textRisk + " Visual:" + visualRisk + " Copyright:" + (copyrightRisk ? "yes" : "no"),
  decision_confidence: compositeScore >= 70 || compositeScore <= 15 ? "high" : "medium",
  requires_human: decision === "human_review" ? "yes" : "no"
};`,
          outputVariables: [
            { name: "composite_score", type: "number" },
            { name: "moderation_decision", type: "string" },
            { name: "is_auto_actionable", type: "string" },
            { name: "risk_summary", type: "string" },
            { name: "decision_confidence", type: "string" },
            { name: "requires_human", type: "string" },
          ],
        },
      }),

      // 5. Moderation decision condition
      n("5", "condition", "Can this be auto-moderated?", 1000, {
        conditionData: [
          {
            variable: "is_auto_actionable",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Auto-approve/reject execution
      n("6", "hitApi", "Execute auto-moderation decision", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{platform_api}/api/content/{content_id}/moderate",
          headers: [
            { key: "Authorization", value: "Bearer {platform_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "action", value: "{moderation_decision}" },
            {
              key: "reason",
              value: "Auto-moderated. Score: {composite_score}. {risk_summary}",
            },
            { key: "confidence", value: "{decision_confidence}" },
          ],
          variables: [
            { name: "status", customName: "moderation_status", type: "string" },
            {
              name: "moderated_at",
              customName: "moderated_timestamp",
              type: "string",
            },
          ],
        },
      }),

      // 7. Human moderator queue
      n(
        "7",
        "approvalGate",
        "Human moderator: Review flagged content",
        1240,
        {
          approvalGateDetails: {
            approvers: ["{moderator_email}"],
            message:
              "Content requires human review.\\n\\nContent ID: {content_id}\\nAuthor: {author_id}\\nType: {content_type}\\nComposite score: {composite_score}\\nRisk: {risk_summary}\\nFlagged phrases: {flagged_phrases_json}\\nVisual flags: {visual_flags_json}\\n\\nText: {content_text}\\n\\nApprove to publish, reject to remove.",
            timeout: 24,
          },
        },
        400,
      ),

      // 8. Appeal handling
      aiNode(
        "8",
        "AI: Generate moderation explanation for appeals",
        1480,
        "You write clear, professional moderation decision explanations. Be transparent about what was flagged without revealing exact detection mechanisms. Return JSON.",
        "Generate a moderation explanation for content {content_id}. Decision: {moderation_decision}. Score: {composite_score}. Risk: {risk_summary}. Flagged phrases: {flagged_phrases_json}. Return explanation_text (clear, professional explanation), appeal_eligible (yes/no), policy_references (array of relevant policies), improvement_suggestions (array of how to fix and resubmit).",
        [
          {
            field: "explanation_text",
            as: "moderation_explanation",
            type: "string",
          },
          { field: "appeal_eligible", as: "appeal_eligible", type: "string" },
          {
            field: "policy_references",
            as: "policy_refs_json",
            type: "string",
          },
          {
            field: "improvement_suggestions",
            as: "improvement_suggestions_json",
            type: "string",
          },
        ],
      ),

      // 9. Moderation metrics report (Sheets)
      sheetsNode(
        "9",
        "Log moderation metrics (Sheets)",
        1720,
        "ModerationLog",
        '[["{content_id}","{content_type}","{author_id}","{composite_score}","{moderation_decision}","{risk_summary}","{decision_confidence}","{requires_human}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // auto-actionable -> execute
      e("5", "7", "output_2"), // needs human -> queue for review
      e("6", "8"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 942. CONTENT PERFORMANCE SCORER (8 nodes)
  // ===================================================================

  "Content Performance Scorer": {
    summary:
      "Runs weekly scoring, fetches engagement, social, and SEO metrics, calculates composite scores via runCode, generates AI performance insights, checks for significant changes via condition, and distributes scorecards via email and Sheets.",
    nodes: [
      trigger("Schedule: Weekly content performance scoring", "schedule", {
        schedule: "0 8 * * 1 (every Monday at 8 AM)",
        howItStarts:
          "A weekly schedule triggers comprehensive content performance scoring across engagement, social, and SEO dimensions to identify top performers and underperformers",
        setup:
          "Connect Google Analytics 4 for engagement data, social media APIs for share/engagement metrics, and SerpAPI for SEO ranking data. Configure scoring weights and Sheets dashboard.",
        inputFields: [],
      }),

      // 2. Engagement metrics fetch
      ga4Node(
        "2",
        "GA4: Fetch content engagement metrics",
        280,
        '[{"name":"pagePath"}]',
        '[{"name":"screenPageViews"},{"name":"averageSessionDuration"},{"name":"bounceRate"},{"name":"conversions"}]',
        [
          { name: "rows", customName: "engagement_data_json", type: "string" },
          { name: "rowCount", customName: "pages_with_data", type: "number" },
        ],
      ),

      // 3. Social metrics fetch
      n("3", "hitApi", "Fetch social sharing metrics", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.sharedcount.com/v1.0/?apikey={sharedcount_key}&url={site_url}",
          headers: [],
          body: [],
          variables: [
            {
              name: "Facebook.total_count",
              customName: "facebook_shares",
              type: "number",
            },
            { name: "Twitter", customName: "twitter_shares", type: "number" },
            { name: "LinkedIn", customName: "linkedin_shares", type: "number" },
            {
              name: "Pinterest",
              customName: "pinterest_shares",
              type: "number",
            },
          ],
        },
      }),

      // 4. SEO performance fetch (SerpAPI)
      serpApiNode(
        "4",
        "SerpAPI: Fetch SEO ranking positions",
        760,
        "site:{our_domain} {primary_keywords}",
        [
          {
            name: "organic_results",
            customName: "seo_rankings_json",
            type: "string",
          },
          {
            name: "organic_results.length",
            customName: "ranked_pages_count",
            type: "number",
          },
          {
            name: "search_information.total_results",
            customName: "total_indexed_pages",
            type: "number",
          },
        ],
      ),

      // 5. Composite score calculation (runCode)
      n(
        "5",
        "runCode",
        "Calculate composite content performance scores",
        1000,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Calculate composite performance scores for each content piece
const engagement = JSON.parse(vars.engagement_data_json || "[]");
const fbShares = parseInt(vars.facebook_shares) || 0;
const twShares = parseInt(vars.twitter_shares) || 0;
const liShares = parseInt(vars.linkedin_shares) || 0;
const totalSocial = fbShares + twShares + liShares;
const seoRankings = JSON.parse(vars.seo_rankings_json || "[]");
const scores = [];
const pageCount = engagement.length || 0;
let totalComposite = 0;
for (let i = 0; i < Math.min(pageCount, 50); i++) {
  const page = engagement[i] || {};
  const views = parseInt(page.screenPageViews || page.dimensionValues?.[1]?.value) || 0;
  const duration = parseFloat(page.averageSessionDuration || "0");
  const bounce = parseFloat(page.bounceRate || "50");
  const conversions = parseInt(page.conversions || "0");
  // Weighted scoring (0-100)
  const engagementScore = Math.min(views / 100, 30) + Math.min(duration / 10, 20) + Math.max(20 - bounce / 5, 0);
  const socialScore = Math.min(totalSocial / 10, 25);
  const seoScore = Math.min(seoRankings.length * 5, 25);
  const composite = Math.round(engagementScore + socialScore + seoScore);
  totalComposite += composite;
  scores.push({ page: page.pagePath || "page_" + i, composite, engagementScore: Math.round(engagementScore), socialScore: Math.round(socialScore), seoScore: Math.round(seoScore) });
}
scores.sort((a, b) => b.composite - a.composite);
const avgScore = pageCount > 0 ? Math.round(totalComposite / pageCount) : 0;
const topPerformer = scores[0] || { page: "none", composite: 0 };
const underperformers = scores.filter(s => s.composite < avgScore * 0.5);
return {
  scored_content_json: JSON.stringify(scores.slice(0, 20)),
  total_scored: scores.length,
  avg_composite_score: avgScore,
  top_performer_page: topPerformer.page,
  top_performer_score: topPerformer.composite,
  underperformer_count: underperformers.length,
  significant_change: Math.abs(avgScore - (parseInt(vars.last_avg_score) || avgScore)) > 10 ? "yes" : "no"
};`,
            outputVariables: [
              { name: "scored_content_json", type: "string" },
              { name: "total_scored", type: "number" },
              { name: "avg_composite_score", type: "number" },
              { name: "top_performer_page", type: "string" },
              { name: "top_performer_score", type: "number" },
              { name: "underperformer_count", type: "number" },
              { name: "significant_change", type: "string" },
            ],
          },
        },
      ),

      // 6. AI performance insights
      aiNode(
        "6",
        "AI: Generate content performance insights",
        1240,
        "You are a content performance analyst who identifies trends, explains performance drivers, and recommends optimizations. Be specific and actionable. Return JSON.",
        "Analyze content performance scores: {scored_content_json}. Total scored: {total_scored}. Avg score: {avg_composite_score}. Top performer: {top_performer_page} ({top_performer_score}). Underperformers: {underperformer_count}. Social metrics - FB:{facebook_shares} TW:{twitter_shares} LI:{linkedin_shares}. SEO ranked pages: {ranked_pages_count}. Return performance_summary (executive overview), top_performer_analysis (why it works), underperformer_recommendations (array of specific actions), trend_direction (improving/stable/declining), key_insight (single most important finding), optimization_priorities (ranked array).",
        [
          {
            field: "performance_summary",
            as: "performance_summary",
            type: "string",
          },
          {
            field: "top_performer_analysis",
            as: "top_performer_analysis",
            type: "string",
          },
          {
            field: "underperformer_recommendations",
            as: "underperformer_recs_json",
            type: "string",
          },
          { field: "trend_direction", as: "trend_direction", type: "string" },
          { field: "key_insight", as: "key_insight", type: "string" },
          {
            field: "optimization_priorities",
            as: "optimization_priorities_json",
            type: "string",
          },
        ],
      ),

      // 7. Significant change condition
      n("7", "condition", "Was there a significant score change?", 1480, {
        conditionData: [
          {
            variable: "significant_change",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Scorecard distribution (email + Sheets)
      emailNode(
        "8",
        "Email: Distribute content performance scorecard",
        1720,
        "{marketing_team_email}",
        "Weekly Content Scorecard — Avg: {avg_composite_score} | Trend: {trend_direction}",
        "Weekly Content Performance Scorecard\\n\\n{performance_summary}\\n\\nKey Metrics:\\n- Content pieces scored: {total_scored}\\n- Average composite score: {avg_composite_score}\\n- Top performer: {top_performer_page} (score: {top_performer_score})\\n- Underperformers: {underperformer_count}\\n- Trend: {trend_direction}\\n\\nKey insight: {key_insight}\\n\\nTop performer analysis: {top_performer_analysis}\\n\\nFull scorecard available in Google Sheets dashboard.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // significant change -> alert email
      e("7", "8", "output_2"), // no significant change -> routine email
    ],
  },

  // ===================================================================
  // 943. CONTENT REPURPOSER (7 nodes)
  // ===================================================================

  "Content Repurposer": {
    summary:
      "Triggers on blog publication, fetches content, repurposes into multiple formats with AI, adapts platform tone, routes through content review approval, schedules social and email distribution via Buffer, and tracks repurposing performance in Sheets.",
    nodes: [
      trigger("Webhook: Blog post published", "webhook_app", {
        source: "WordPress / CMS",
        event: "post.published",
        howItStarts:
          "A new blog post is published in the CMS, triggering automatic repurposing into social media posts, email snippets, video scripts, and infographic outlines",
        setup:
          "Configure CMS to fire webhook on post publish. Connect Buffer for social scheduling. Set up content review Slack channel and Sheets performance tracker.",
        inputFields: [],
      }),

      // 2. Content fetch
      aiNode(
        "2",
        "AI: Extract published blog content",
        280,
        "You extract blog post content from CMS webhook payloads regardless of format. Return JSON with the full content and metadata.",
        "Extract blog post data from this webhook: {webhook_payload}. Return post_id (string), post_title (string), post_body (full text), post_url (published URL), author_name (string), post_category (string), post_tags (comma-separated), publish_date (ISO), word_count (number), key_takeaways (array of 3-5 main points).",
        [
          { field: "post_id", as: "post_id", type: "string" },
          { field: "post_title", as: "post_title", type: "string" },
          { field: "post_body", as: "post_body", type: "string" },
          { field: "post_url", as: "post_url", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "post_category", as: "post_category", type: "string" },
          { field: "post_tags", as: "post_tags", type: "string" },
          { field: "key_takeaways", as: "key_takeaways_json", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
        ],
      ),

      // 3. AI multi-format repurposing
      aiNode(
        "3",
        "AI: Repurpose into multiple content formats",
        520,
        "You are a content repurposing expert who transforms long-form blog content into multiple formats while preserving key messages. Each format should feel native to its platform. Return JSON.",
        "Repurpose this blog post into multiple formats. Title: {post_title}. Body: {post_body}. Key takeaways: {key_takeaways_json}. Tags: {post_tags}. Generate: twitter_thread (5-7 tweets), linkedin_post (professional 200 words), instagram_caption (casual with emojis, 150 words), email_newsletter_snippet (100 words with CTA), video_script_outline (60-second talking points), infographic_data_points (array of visual data points), podcast_talking_points (array). Return all fields plus total_formats (number).",
        [
          { field: "twitter_thread", as: "twitter_thread", type: "string" },
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          {
            field: "instagram_caption",
            as: "instagram_caption",
            type: "string",
          },
          {
            field: "email_newsletter_snippet",
            as: "email_snippet",
            type: "string",
          },
          { field: "video_script_outline", as: "video_script", type: "string" },
          { field: "total_formats", as: "total_formats", type: "number" },
        ],
      ),

      // 4. Platform tone adaptation
      aiNode(
        "4",
        "AI: Final platform tone polish",
        760,
        "You refine content for each platform ensuring hashtags, mentions, character limits, and tone are platform-perfect. Return JSON.",
        "Polish these repurposed formats for platform best practices. Twitter thread: {twitter_thread}. LinkedIn: {linkedin_post}. Instagram: {instagram_caption}. Email: {email_snippet}. Blog URL: {post_url}. Tags: {post_tags}. Return polished_twitter (with hashtags, within character limits), polished_linkedin (with appropriate formatting), polished_instagram (with hashtags and CTA), polished_email (with clear CTA and link), platform_notes (any adjustments made).",
        [
          { field: "polished_twitter", as: "final_twitter", type: "string" },
          { field: "polished_linkedin", as: "final_linkedin", type: "string" },
          {
            field: "polished_instagram",
            as: "final_instagram",
            type: "string",
          },
          { field: "polished_email", as: "final_email", type: "string" },
          { field: "platform_notes", as: "platform_notes", type: "string" },
        ],
      ),

      // 5. Content review approval gate
      n(
        "5",
        "approvalGate",
        "Content review: Approve repurposed content",
        1000,
        {
          approvalGateDetails: {
            approvers: ["{content_manager_email}"],
            message:
              "Blog repurposed into {total_formats} formats.\\n\\nOriginal: {post_title}\\nURL: {post_url}\\n\\nTwitter preview: {final_twitter}\\nLinkedIn preview: {final_linkedin}\\n\\nPlatform notes: {platform_notes}\\n\\nApprove to schedule across all channels.",
            timeout: 24,
          },
        },
      ),

      // 6. Social/email scheduling (Buffer)
      bufferNode(
        "6",
        "Buffer: Schedule repurposed social posts",
        1240,
        "{final_twitter}",
      ),

      // 7. Repurposing performance tracking (Sheets)
      sheetsNode(
        "7",
        "Track repurposing performance (Sheets)",
        1480,
        "RepurposingTracker",
        '[["{post_id}","{post_title}","{post_url}","{author_name}","{total_formats}","{post_category}","{word_count}","scheduled","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 944. CONTENT REPURPOSING BOT (8 nodes)
  // ===================================================================

  "Content Repurposing Bot": {
    summary:
      "Receives repurposing requests, fetches and parses source content, analyzes structure with AI, refines conversationally via sendMessage, generates multi-format outputs with AI, validates format compliance via condition, delivers outputs, and logs requests to Sheets.",
    nodes: [
      trigger("Webhook: Content repurposing request", "webhook_app", {
        source: "Slack / Internal Tools",
        event: "content.repurpose.requested",
        howItStarts:
          "A team member requests content repurposing via Slack command or internal tool, specifying the source content URL and desired output formats",
        setup:
          "Configure Slack slash command or internal tool to POST repurposing requests. Set up content fetching and parsing capabilities. Configure delivery channels.",
        inputFields: [],
      }),

      // 2. Source content fetch + parse
      aiNode(
        "2",
        "AI: Parse repurposing request and fetch content",
        280,
        "You parse content repurposing requests regardless of format. Extract source URL, desired formats, and preferences. Return JSON.",
        "Parse this repurposing request: {webhook_payload}. Return source_url (content URL), source_type (blog/video/podcast/whitepaper), desired_formats (comma-separated output formats), target_audience (who the repurposed content is for), tone_preference (professional/casual/educational), requester_name (string), requester_channel (Slack channel for delivery), special_instructions (any specific requests).",
        [
          { field: "source_url", as: "source_url", type: "string" },
          { field: "source_type", as: "source_type", type: "string" },
          { field: "desired_formats", as: "desired_formats", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "tone_preference", as: "tone_preference", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          {
            field: "requester_channel",
            as: "requester_channel",
            type: "string",
          },
          {
            field: "special_instructions",
            as: "special_instructions",
            type: "string",
          },
        ],
      ),

      // 3. AI content structure analysis
      aiNode(
        "3",
        "AI: Analyze content structure and key elements",
        520,
        "You are a content analyst who deconstructs content into its core elements: thesis, supporting arguments, data points, quotes, and narrative arc. Return JSON.",
        "Analyze the structure of content from {source_url} (type: {source_type}). Extract content_title (string), main_thesis (core argument), key_arguments (array of 3-5 supporting points), data_points (array of statistics/facts), notable_quotes (array), narrative_arc (intro/body/conclusion summary), content_length (word/minute estimate), complexity_level (simple/moderate/complex), emotional_hooks (array).",
        [
          { field: "content_title", as: "content_title", type: "string" },
          { field: "main_thesis", as: "main_thesis", type: "string" },
          { field: "key_arguments", as: "key_arguments_json", type: "string" },
          { field: "data_points", as: "data_points_json", type: "string" },
          {
            field: "notable_quotes",
            as: "notable_quotes_json",
            type: "string",
          },
          { field: "complexity_level", as: "complexity_level", type: "string" },
          {
            field: "emotional_hooks",
            as: "emotional_hooks_json",
            type: "string",
          },
        ],
      ),

      // 4. Conversational refinement (sendMessage)
      n("4", "sendMessage", "Refine repurposing approach with requester", 760, {
        sendMessageDetails: {
          channel: "{requester_channel}",
          message:
            "I have analyzed the content from {source_url}.\\n\\nTitle: {content_title}\\nMain thesis: {main_thesis}\\nComplexity: {complexity_level}\\n\\nI will repurpose into: {desired_formats}\\nTone: {tone_preference}\\nAudience: {target_audience}\\n\\nSpecial instructions: {special_instructions}\\n\\nProceeding with generation now. You will receive the outputs shortly.",
        },
      }),

      // 5. AI multi-format output generation
      aiNode(
        "5",
        "AI: Generate multi-format repurposed content",
        1000,
        "You are a multi-format content creator. Transform analyzed content into each requested format while maintaining core messaging. Adapt tone and style for each platform. Return JSON.",
        "Generate repurposed content. Title: {content_title}. Thesis: {main_thesis}. Key arguments: {key_arguments_json}. Data points: {data_points_json}. Quotes: {notable_quotes_json}. Emotional hooks: {emotional_hooks_json}. Desired formats: {desired_formats}. Tone: {tone_preference}. Audience: {target_audience}. Instructions: {special_instructions}. For each format return format_name, content (full text), character_count, platform_ready (yes/no). Return outputs (array), total_outputs (number), formats_generated (comma-separated), all_valid (yes/no).",
        [
          { field: "outputs", as: "repurposed_outputs_json", type: "string" },
          { field: "total_outputs", as: "total_outputs", type: "number" },
          {
            field: "formats_generated",
            as: "formats_generated",
            type: "string",
          },
          { field: "all_valid", as: "all_formats_valid", type: "string" },
        ],
      ),

      // 6. Format validation condition
      n("6", "condition", "Are all output formats valid?", 1240, {
        conditionData: [
          {
            variable: "all_formats_valid",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Delivery of outputs
      slackNode(
        "7",
        "Slack: Deliver repurposed content outputs",
        1480,
        "CONTENT REPURPOSED\\n\\nSource: {content_title} ({source_url})\\nFormats: {formats_generated}\\nTotal outputs: {total_outputs}\\n\\n{repurposed_outputs_json}\\n\\nRequested by: {requester_name} | Tone: {tone_preference} | Audience: {target_audience}",
      ),

      // 8. Request logging (Sheets)
      sheetsNode(
        "8",
        "Log repurposing request (Sheets)",
        1720,
        "RepurposingLog",
        '[["{requester_name}","{source_url}","{source_type}","{content_title}","{desired_formats}","{formats_generated}","{total_outputs}","{all_formats_valid}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // all valid -> deliver
      e("6", "5", "output_2"), // invalid -> regenerate
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 945. CONTENT REPURPOSING ENGINE (8 nodes)
  // ===================================================================

  "Content Repurposing Engine": {
    summary:
      "Triggers on transformation request, fetches source content and metadata, decomposes content with AI, applies format-specific transformations, optimizes for platforms via runCode, runs AI quality and consistency checks, creates production tasks in Asana, and delivers transformation packages via Slack.",
    nodes: [
      trigger("Webhook: Content transformation request", "webhook_app", {
        source: "Content Management System / Slack",
        event: "content.transform.requested",
        howItStarts:
          "A content transformation request is submitted specifying the source content, target formats, and quality requirements for enterprise-grade content repurposing",
        setup:
          "Configure content ingestion pipeline. Set up format-specific templates and brand guidelines. Connect Asana for production task management. Configure Slack for package delivery.",
        inputFields: [],
      }),

      // 2. Source content + metadata fetch
      aiNode(
        "2",
        "AI: Extract transformation request details",
        280,
        "You parse content transformation requests regardless of format. Extract source details, target specifications, and quality requirements. Return JSON.",
        "Parse this transformation request: {webhook_payload}. Return source_content_id (string), source_content_body (full text if embedded), source_url (URL if linked), source_format (blog/video_transcript/podcast_transcript/whitepaper/webinar), target_formats (array of desired output formats), quality_tier (standard/premium/enterprise), brand_guidelines_ref (string), deadline (ISO date), requester_email (string), project_name (string).",
        [
          { field: "source_content_id", as: "source_id", type: "string" },
          { field: "source_content_body", as: "source_body", type: "string" },
          { field: "source_format", as: "source_format", type: "string" },
          {
            field: "target_formats",
            as: "target_formats_json",
            type: "string",
          },
          { field: "quality_tier", as: "quality_tier", type: "string" },
          {
            field: "brand_guidelines_ref",
            as: "brand_guidelines",
            type: "string",
          },
          { field: "deadline", as: "deadline", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "project_name", as: "project_name", type: "string" },
        ],
      ),

      // 3. AI content decomposition
      aiNode(
        "3",
        "AI: Decompose content into reusable components",
        520,
        "You are a content architect who decomposes content into atomic, reusable components that can be reassembled into different formats. Return JSON.",
        "Decompose this content for multi-format repurposing. Source format: {source_format}. Content: {source_body}. Quality tier: {quality_tier}. Return headline_variants (array of 5 headlines), core_message (50 words), extended_summary (200 words), key_stats (array of data points), pull_quotes (array), section_summaries (array of {section, summary}), cta_variants (array of 3 CTAs), visual_concepts (array of image/graphic ideas), seo_keywords (array), content_atoms (array of smallest reusable units).",
        [
          { field: "headline_variants", as: "headlines_json", type: "string" },
          { field: "core_message", as: "core_message", type: "string" },
          { field: "extended_summary", as: "extended_summary", type: "string" },
          { field: "key_stats", as: "key_stats_json", type: "string" },
          { field: "pull_quotes", as: "pull_quotes_json", type: "string" },
          {
            field: "section_summaries",
            as: "section_summaries_json",
            type: "string",
          },
          { field: "cta_variants", as: "cta_variants_json", type: "string" },
          { field: "content_atoms", as: "content_atoms_json", type: "string" },
        ],
      ),

      // 4. Format-specific transformations
      aiNode(
        "4",
        "AI: Generate format-specific content versions",
        760,
        "You transform decomposed content components into polished format-specific outputs. Each output should be production-ready for its target format. Return JSON.",
        "Transform decomposed content into these formats: {target_formats_json}. Core message: {core_message}. Summary: {extended_summary}. Headlines: {headlines_json}. Stats: {key_stats_json}. Quotes: {pull_quotes_json}. CTAs: {cta_variants_json}. Atoms: {content_atoms_json}. Quality: {quality_tier}. Brand: {brand_guidelines}. For each format return format_type, content (production-ready), word_count, platform_specs_met (yes/no). Return transformations (array), total_transformations (number), all_specs_met (yes/no).",
        [
          {
            field: "transformations",
            as: "transformations_json",
            type: "string",
          },
          {
            field: "total_transformations",
            as: "total_transformations",
            type: "number",
          },
          { field: "all_specs_met", as: "all_specs_met", type: "string" },
        ],
      ),

      // 5. Platform optimization (runCode)
      n("5", "runCode", "Optimize content for platform specifications", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate and optimize each transformation for platform specs
const transformations = JSON.parse(vars.transformations_json || "[]");
const specs = {
  twitter: { maxChars: 280, hashtagLimit: 5 },
  linkedin: { maxChars: 3000, hashtagLimit: 5 },
  instagram: { maxChars: 2200, hashtagLimit: 30 },
  email: { maxWords: 500, ctaRequired: true },
  blog_summary: { maxWords: 300 },
  video_script: { maxSeconds: 60 },
  infographic: { maxDataPoints: 10 }
};
const optimized = transformations.map(t => {
  const spec = specs[t.format_type] || {};
  let issues = [];
  if (spec.maxChars && t.content.length > spec.maxChars) {
    issues.push("Exceeds " + spec.maxChars + " char limit");
  }
  if (spec.maxWords && t.word_count > spec.maxWords) {
    issues.push("Exceeds " + spec.maxWords + " word limit");
  }
  return {
    ...t,
    platform_optimized: issues.length === 0,
    optimization_issues: issues,
    spec_compliance: issues.length === 0 ? "pass" : "needs_trim"
  };
});
const allOptimized = optimized.every(o => o.platform_optimized);
return {
  optimized_transformations_json: JSON.stringify(optimized),
  all_optimized: allOptimized ? "yes" : "no",
  issues_count: optimized.filter(o => !o.platform_optimized).length,
  total_optimized: optimized.length
};`,
          outputVariables: [
            { name: "optimized_transformations_json", type: "string" },
            { name: "all_optimized", type: "string" },
            { name: "issues_count", type: "number" },
            { name: "total_optimized", type: "number" },
          ],
        },
      }),

      // 6. AI quality + consistency check
      aiNode(
        "6",
        "AI: Quality and brand consistency check",
        1240,
        "You are a brand quality controller who ensures all content transformations maintain consistent messaging, tone, and brand voice. Return JSON.",
        "Quality check these optimized transformations: {optimized_transformations_json}. Core message: {core_message}. Brand guidelines: {brand_guidelines}. Quality tier: {quality_tier}. For each check brand_voice_score (1-10), message_consistency (1-10), quality_score (1-10). Return qa_results (array), overall_quality_score (number), brand_consistency_score (number), quality_passed (yes/no), improvement_notes (array).",
        [
          { field: "qa_results", as: "qa_results_json", type: "string" },
          {
            field: "overall_quality_score",
            as: "overall_quality_score",
            type: "number",
          },
          {
            field: "brand_consistency_score",
            as: "brand_consistency_score",
            type: "number",
          },
          { field: "quality_passed", as: "quality_passed", type: "string" },
          {
            field: "improvement_notes",
            as: "improvement_notes_json",
            type: "string",
          },
        ],
      ),

      // 7. Production task creation (Asana)
      asanaNode(
        "7",
        "Create production tasks in Asana",
        1480,
        "{project_name}: {total_transformations} content transformations",
        "Content repurposing package ready for production.\\n\\nSource: {source_id} ({source_format})\\nTransformations: {total_transformations}\\nQuality tier: {quality_tier}\\nQuality score: {overall_quality_score}/10\\nBrand consistency: {brand_consistency_score}/10\\nDeadline: {deadline}\\n\\nAll transformations passed quality check: {quality_passed}",
      ),

      // 8. Transformation package delivery (Slack)
      slackNode(
        "8",
        "Slack: Deliver transformation package",
        1720,
        "CONTENT TRANSFORMATION PACKAGE READY\\n\\nProject: {project_name}\\nSource: {source_id} ({source_format})\\nTransformations: {total_transformations} formats\\nQuality: {overall_quality_score}/10 | Brand: {brand_consistency_score}/10\\nPlatform optimized: {all_optimized}\\n\\nAsana tasks: {asana_task_url}\\nDeadline: {deadline}\\n\\nCore message: {core_message}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 946. CONVERSATIONAL PREFERENCE-CAPTURE WELCOME (7 nodes)
  // ===================================================================

  "Conversational Preference-Capture Welcome": {
    summary:
      "Triggers on new subscriber, fetches signup context, generates a personalized AI welcome email with preference CTA, sends the welcome email, monitors preference center engagement, checks if preferences were set via condition, and confirms preferences with downstream routing.",
    nodes: [
      trigger("Webhook: New subscriber added", "webhook_app", {
        source: "Email Platform / CRM / Signup Form",
        event: "subscriber.created",
        howItStarts:
          "A new subscriber is added to the email list through signup form, CRM integration, or import — triggering a conversational welcome sequence that captures content preferences",
        setup:
          "Configure email platform or CRM to fire webhook on new subscriber creation. Set up preference center URL. Design welcome email template with preference CTA.",
        inputFields: [],
      }),

      // 2. Signup context fetch
      aiNode(
        "2",
        "AI: Extract subscriber signup context",
        280,
        "You extract subscriber data from webhook payloads regardless of format. Return JSON with subscriber details and signup context.",
        "Extract subscriber data from this webhook: {webhook_payload}. Return subscriber_email (string), subscriber_name (string, or empty), signup_source (where they signed up), signup_date (ISO), referring_content (what content led to signup if available), initial_interests (any indicated interests), utm_source (string), utm_medium (string), utm_campaign (string), company_name (if B2B).",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          {
            field: "referring_content",
            as: "referring_content",
            type: "string",
          },
          {
            field: "initial_interests",
            as: "initial_interests",
            type: "string",
          },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
        ],
      ),

      // 3. AI welcome email with preference CTA
      aiNode(
        "3",
        "AI: Generate personalized welcome with preference CTA",
        520,
        "You write warm, conversational welcome emails that make subscribers feel valued while naturally guiding them to set content preferences. The preference CTA should feel helpful, not demanding. Return JSON.",
        "Write a personalized welcome email. Subscriber: {subscriber_name} (or 'there' if empty). Email: {subscriber_email}. Signup source: {signup_source}. Referring content: {referring_content}. Initial interests: {initial_interests}. UTM campaign: {utm_campaign}. Return welcome_subject (warm, curiosity-driving), welcome_body_html (conversational HTML email with embedded preference questions — frequency, topics, content type — and a prominent preference center CTA), preference_center_url (placeholder with subscriber token), primary_cta_text (button text), tone_note (what tone was used and why).",
        [
          { field: "welcome_subject", as: "welcome_subject", type: "string" },
          {
            field: "welcome_body_html",
            as: "welcome_body_html",
            type: "string",
          },
          {
            field: "preference_center_url",
            as: "preference_url",
            type: "string",
          },
          { field: "primary_cta_text", as: "cta_text", type: "string" },
          { field: "tone_note", as: "tone_note", type: "string" },
        ],
      ),

      // 4. Welcome email send
      emailNode(
        "4",
        "Send personalized welcome email",
        760,
        "{subscriber_email}",
        "{welcome_subject}",
        "{welcome_body_html}",
      ),

      // 5. Preference center engagement monitoring
      n("5", "delayAction", "Wait for preference center engagement", 1000, {
        delayActionDetails: {
          delayType: "fixed",
          delayValue: 48,
          delayUnit: "hours",
          description:
            "Wait 48 hours for the subscriber to visit the preference center and set their content preferences before following up.",
        },
      }),

      // 6. Preferences set condition
      n("6", "condition", "Did subscriber set preferences?", 1240, {
        conditionData: [
          {
            variable: "initial_interests",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Preference confirmation + downstream routing
      aiNode(
        "7",
        "AI: Confirm preferences and route subscriber",
        1480,
        "You write brief, appreciative preference confirmation messages and determine the best content stream for a subscriber. Return JSON.",
        "Generate preference confirmation for {subscriber_name}. Email: {subscriber_email}. Source: {signup_source}. Interests: {initial_interests}. Referring content: {referring_content}. Return confirmation_subject (brief thank you), confirmation_body (short HTML confirming their preferences and previewing what they will receive), recommended_segment (which email segment to add them to), first_content_recommendation (specific content piece to send next based on interests), engagement_prediction (high/medium/low based on signals).",
        [
          {
            field: "confirmation_subject",
            as: "confirmation_subject",
            type: "string",
          },
          {
            field: "confirmation_body",
            as: "confirmation_body",
            type: "string",
          },
          {
            field: "recommended_segment",
            as: "recommended_segment",
            type: "string",
          },
          {
            field: "first_content_recommendation",
            as: "first_content_rec",
            type: "string",
          },
          {
            field: "engagement_prediction",
            as: "engagement_prediction",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // preferences set -> confirm and route
      e("6", "3", "output_2"), // no preferences -> resend welcome variant
    ],
  },

  // ===================================================================
  // 947. BRAND VOICE ANALYZER (7 nodes)
  // ===================================================================

  "Brand Voice Analyzer": {
    summary:
      "Triggers on content submission, fetches brand voice guidelines, analyzes content voice with AI, compares against benchmarks, generates AI improvement suggestions, builds a brand voice scorecard, and delivers the analysis report via email.",
    nodes: [
      trigger(
        "Webhook: Content submitted for brand voice analysis",
        "webhook_app",
        {
          source: "CMS / Content Platform / Slack",
          event: "content.voice.analysis.requested",
          howItStarts:
            "Content is submitted for brand voice analysis — either automatically before publish or manually by a writer seeking feedback on voice and tone alignment",
          setup:
            "Configure content submission webhook. Maintain brand voice guidelines document (tone, vocabulary, sentence structure, personality traits). Set up benchmark content library for comparison.",
          inputFields: [],
        },
      ),

      // 2. Brand voice guidelines fetch
      aiNode(
        "2",
        "AI: Extract content and parse submission",
        280,
        "You extract content submitted for brand voice analysis regardless of format. Return JSON with the content and context.",
        "Extract content for brand voice analysis from this submission: {webhook_payload}. Return content_id (string), content_text (the full submitted text), content_type (blog/email/social/landing/ad), author_email (string), author_name (string), target_audience (who the content is for), channel (where it will be published), word_count (number).",
        [
          { field: "content_id", as: "content_id", type: "string" },
          { field: "content_text", as: "content_text", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "author_email", as: "author_email", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "channel", as: "publish_channel", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
        ],
      ),

      // 3. AI content voice analysis
      aiNode(
        "3",
        "AI: Analyze content voice characteristics",
        520,
        "You are a brand voice analyst who evaluates content across multiple voice dimensions. Assess tone, vocabulary, sentence structure, personality, and emotional resonance. Return JSON.",
        "Analyze the brand voice of this content. Text: {content_text}. Type: {content_type}. Audience: {target_audience}. Channel: {publish_channel}. Evaluate: tone_formality (1-10 casual to formal), tone_confidence (1-10), vocabulary_complexity (1-10 simple to complex), sentence_avg_length (words), active_voice_pct (percentage), jargon_density (1-10), emotional_warmth (1-10), humor_level (1-10), authority_level (1-10), inclusivity_score (1-10). Return all scores plus voice_fingerprint (JSON object of all scores), dominant_trait (primary voice characteristic), voice_summary (50-word characterization).",
        [
          { field: "tone_formality", as: "tone_formality", type: "number" },
          { field: "tone_confidence", as: "tone_confidence", type: "number" },
          {
            field: "vocabulary_complexity",
            as: "vocab_complexity",
            type: "number",
          },
          { field: "active_voice_pct", as: "active_voice_pct", type: "number" },
          { field: "emotional_warmth", as: "emotional_warmth", type: "number" },
          { field: "authority_level", as: "authority_level", type: "number" },
          { field: "dominant_trait", as: "dominant_trait", type: "string" },
          { field: "voice_summary", as: "voice_summary", type: "string" },
          {
            field: "voice_fingerprint",
            as: "voice_fingerprint_json",
            type: "string",
          },
        ],
      ),

      // 4. Benchmark comparison
      aiNode(
        "4",
        "AI: Compare against brand voice benchmarks",
        760,
        "You compare analyzed content voice against established brand voice benchmarks. Identify deviations and alignment. Return JSON.",
        "Compare voice analysis against brand benchmarks. Content voice: {voice_fingerprint_json}. Content type: {content_type}. Channel: {publish_channel}. Brand guidelines target: formality 4-6, confidence 7-9, complexity 3-5, warmth 7-9, authority 6-8, active voice >70%. Return alignment_score (0-100 overall), deviations (array of {dimension, current, target, gap, severity}), strongest_alignment (dimension closest to target), largest_deviation (dimension furthest from target), benchmark_comparison_table (markdown table), overall_assessment (on-brand/slightly-off/significantly-off).",
        [
          { field: "alignment_score", as: "alignment_score", type: "number" },
          { field: "deviations", as: "deviations_json", type: "string" },
          {
            field: "strongest_alignment",
            as: "strongest_alignment",
            type: "string",
          },
          {
            field: "largest_deviation",
            as: "largest_deviation",
            type: "string",
          },
          {
            field: "benchmark_comparison_table",
            as: "benchmark_table",
            type: "string",
          },
          {
            field: "overall_assessment",
            as: "brand_assessment",
            type: "string",
          },
        ],
      ),

      // 5. AI improvement suggestions
      aiNode(
        "5",
        "AI: Generate voice improvement suggestions",
        1000,
        "You provide specific, actionable suggestions to bring content closer to brand voice guidelines. Include before/after examples. Return JSON.",
        "Generate improvement suggestions. Content: {content_text}. Voice summary: {voice_summary}. Dominant trait: {dominant_trait}. Alignment: {alignment_score}/100. Assessment: {brand_assessment}. Deviations: {deviations_json}. Largest deviation: {largest_deviation}. Provide specific_edits (array of {location_hint, original_phrase, suggested_phrase, reason}), general_tips (array of 3-5 tips), priority_fix (the single most impactful change), estimated_improvement (how many alignment points the fixes would add), rewrite_example (a rewritten paragraph demonstrating ideal voice).",
        [
          {
            field: "specific_edits",
            as: "specific_edits_json",
            type: "string",
          },
          { field: "general_tips", as: "general_tips_json", type: "string" },
          { field: "priority_fix", as: "priority_fix", type: "string" },
          {
            field: "estimated_improvement",
            as: "estimated_improvement",
            type: "number",
          },
          { field: "rewrite_example", as: "rewrite_example", type: "string" },
        ],
      ),

      // 6. Brand voice scorecard
      sheetsNode(
        "6",
        "Log brand voice scorecard (Sheets)",
        1240,
        "BrandVoiceScores",
        '[["{content_id}","{content_type}","{author_name}","{publish_channel}","{alignment_score}","{brand_assessment}","{dominant_trait}","{tone_formality}","{tone_confidence}","{vocab_complexity}","{emotional_warmth}","{authority_level}","{active_voice_pct}","{largest_deviation}","{priority_fix}","=NOW()"]]',
      ),

      // 7. Analysis report delivery (email)
      emailNode(
        "7",
        "Email: Deliver brand voice analysis report",
        1480,
        "{author_email}",
        "Brand Voice Analysis: {brand_assessment} — Score {alignment_score}/100",
        "Brand Voice Analysis Report\\n\\nContent: {content_id} ({content_type} for {publish_channel})\\n\\nOverall Score: {alignment_score}/100 ({brand_assessment})\\n\\nVoice Summary: {voice_summary}\\nDominant Trait: {dominant_trait}\\n\\n{benchmark_table}\\n\\nStrongest Alignment: {strongest_alignment}\\nLargest Deviation: {largest_deviation}\\n\\nPriority Fix: {priority_fix}\\n\\nEstimated improvement with fixes: +{estimated_improvement} points\\n\\nRewrite Example:\\n{rewrite_example}\\n\\nGeneral Tips: {general_tips_json}\\n\\nFull scorecard and specific edits available in your Google Sheets dashboard.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
